import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Wrench, Truck, PackageCheck, Trophy, Filter, LayoutGrid, Send,
    ChevronRight, X, MapPin, Network, Building2, Plus, Pencil, Trash2, Eye, EyeOff,
    ListChecks, Palette, PlugZap, Eraser,
    ShieldCheck, Info, KeyRound, Undo2,
} from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FlowLoader from "../components/FlowLoader";
import DesksAdmin from "../components/DesksAdmin";
import CarrierBaysAdmin from "../components/CarrierBaysAdmin";
import ExtraBoxSetting from "../components/ExtraBoxSetting";
import LocationSizesAdmin from "../components/LocationSizesAdmin";
import LocationTypesAdmin from "../components/LocationTypesAdmin";
import ScansheetLocationsAdmin from "../components/ScansheetLocationsAdmin";
import LeadersBonusSettings from "../components/admin/LeadersBonusSettings";
import AdminPageHeader from "../components/admin-page-header";
import { useDemoMode, setDemoMode, useDemoAudience, setDemoAudience } from "../components/helpers/demoMode";
import PickingAllocationMethod from "../components/admin/PickingAllocationMethod";
import WalkPatternExperiment from "../components/admin/picking/WalkPatternExperiment";
import WarehouseLayoutEditor from "../components/admin/layout/WarehouseLayoutEditor";
import { isAdminPosition } from "../access/positions";
import { useReadOnly } from "../access/ReadOnly";
import { palletwaysTypeLabel, palletwaysTypeTitle } from "../components/pallet-dispatch/palletDesk";
import { useWarehouseScope } from "../access/useWarehouseScope";
import IpAllowlistAdmin from "../components/admin/IpAllowlistAdmin";
import SavedLoginsSetting from "../components/admin/SavedLoginsSetting";
import LaneStatusColoursAdmin from "../components/admin/LaneStatusColoursAdmin";
import SortingSharedLighting from "../components/SortingSharedLighting";
import SortingBayManager from "../components/SortingBayManager";
import SalesChannelsAdmin from "../components/admin/integrations/SalesChannelsAdmin";
import CarriersAdmin from "../components/admin/integrations/CarriersAdmin";
import ReturnsSettings from "../components/returns/ReturnsSettings";
import ShippingRulesAdmin from "../components/admin/dispatch/ShippingRulesAdmin";
import ChannelRulesAdmin from "../components/admin/dispatch/ChannelRulesAdmin";
import {
    HOME_MODULES,
    ROLES,
    isVisible,
    type Role,
    type HomeModuleVisibilityMap,
    type RoleVisibility,
} from "../components/helpers/homeModuleVisibility";
import {
    ScalarTimerEditor,
    SettingBlock,
    useDebouncedAutoSave,
    type ScalarTimerField,
} from "../components/settings-shared/timerEditors";
import { apiFetch } from "../api/apiFetch";
import "../styles/admin-settings.css";
import "../styles/pallets.css";

// GET /settings/<warehouse>/usage — what still references a warehouse.
// `postgres` and `mongo` block deletion; `derived` is rebuildable cache
// state that goes with the warehouse; `unchecked` is a source we
// couldn't reach, which blocks deletion because we can't prove it's empty.
interface WarehouseUsageEntry {
    name: string;
    count: number;
    capped: boolean;
}
interface WarehouseUsage {
    warehouse: string;
    postgres: WarehouseUsageEntry[];
    mongo: WarehouseUsageEntry[];
    derived: WarehouseUsageEntry[];
    unchecked: string[];
    inUse: boolean;
}

// The pick types that can be enrolled in the walk-pattern experiment.
//
// Large is enrollable but measures much less than the other two: a Large wave
// is JIT, one order at a time, so the stamped arm only reorders the lines
// within a single order (picking-wavebuild.js applies the resolver on JIT
// append). Useful as a check that an arm does no harm there; not a place to
// look for a headline number.
export type PickTypeKey = "pick_and_sort" | "light" | "large";

export interface InboundStockType {
    name: string;
    code: string;
}

export type PalletColourKey = "BLACK" | "GREY" | "RED" | "YELLOW" | "DSD";
// Mirrors PALLET_COLOURS in pages/pallets.tsx; DSD = data-device storage pallets (2026-10-16).
export const PALLET_COLOUR_KEYS: readonly PalletColourKey[] = ["BLACK", "GREY", "RED", "YELLOW", "DSD"];
export interface PalletSettingsStored {
    types?: string[];
    sizes?: string[];
    allow_height?: boolean;
    // Contents warnings (Alex, 2026-10-29): Goods In › Pallets asks whether
    // the pallet holds liquids / glass and prints a warning triangle per
    // hazard on the label. Label only — not stored on the pallet or exported.
    hazard_flags?: boolean;
    next_seq?: Partial<Record<PalletColourKey, number>>;
    // Palletways pallet type per size name (BE migration 202).
    size_palletways?: Record<string, string>;
    // Volumetric weight (Alex, 2026-10-13): when on, a pallet's weight is
    // length × width × height ÷ divisor kg (× 1000 for grams) — Poland's
    // =(120*80*H/4000)*1000 — instead of the sum of what is on it. Height
    // is the pallet's own (sized by height) or its fixed size's below.
    volumetric?: Partial<PalletVolumetric>;
    // Height per fixed size name, cm, for the volumetric sum.
    size_height_cm?: Record<string, number>;
}
export interface PalletVolumetric {
    enabled: boolean;
    length_cm: number;
    width_cm: number;
    divisor: number;
}
export const DEFAULT_PALLET_VOLUMETRIC: PalletVolumetric = { enabled: false, length_cm: 120, width_cm: 80, divisor: 4000 };
export interface PalletSettingsResolved {
    types: string[];
    sizes: string[];
    allow_height: boolean;
    hazard_flags: boolean;
    next_seq: Partial<Record<PalletColourKey, number>>;
    size_palletways: Record<string, string>;
    volumetric: PalletVolumetric;
    size_height_cm: Record<string, number>;
}
const DEFAULT_SIZE_PALLETWAYS: Record<string, string> = { QUARTER: "QP", HALF: "HP", FULL: "LP" };
// Mirrors resolvePalletSettings() in the BE service: stored keys win,
// anything missing takes the default (no types, Quarter/Half/Full, height on).
// size_palletways: only sizes that still exist keep a type; with no map
// stored yet the stock names get QP / HP / LP.
export const resolvePalletSettings = (stored?: PalletSettingsStored | null): PalletSettingsResolved => {
    const sizes = Array.isArray(stored?.sizes) ? stored!.sizes! : ["Quarter", "Half", "Full"];
    const map = stored?.size_palletways && typeof stored.size_palletways === "object" ? stored.size_palletways : null;
    const size_palletways: Record<string, string> = {};
    for (const name of sizes) {
        const type = map
            ? Object.entries(map).find(([k]) => k.toUpperCase() === name.toUpperCase())?.[1]
            : DEFAULT_SIZE_PALLETWAYS[name.toUpperCase()];
        if (type) size_palletways[name] = type;
    }
    const v = stored?.volumetric && typeof stored.volumetric === "object" ? stored.volumetric : {};
    const num = (raw: unknown, dflt: number) => {
        const n = Number(raw);
        return Number.isFinite(n) && n > 0 ? n : dflt;
    };
    const heights = stored?.size_height_cm && typeof stored.size_height_cm === "object" ? stored.size_height_cm : {};
    const size_height_cm: Record<string, number> = {};
    for (const name of sizes) {
        const hit = Object.entries(heights).find(([k]) => k.toUpperCase() === name.toUpperCase());
        if (hit && Number.isFinite(Number(hit[1])) && Number(hit[1]) > 0) size_height_cm[name] = Number(hit[1]);
    }
    return {
        types: Array.isArray(stored?.types) ? stored!.types! : [],
        sizes,
        allow_height: typeof stored?.allow_height === "boolean" ? stored!.allow_height! : true,
        hazard_flags: typeof stored?.hazard_flags === "boolean" ? stored!.hazard_flags! : false,
        next_seq: stored?.next_seq && typeof stored.next_seq === "object" ? { ...stored.next_seq } : {},
        size_palletways,
        volumetric: {
            enabled: typeof v.enabled === "boolean" ? v.enabled : DEFAULT_PALLET_VOLUMETRIC.enabled,
            length_cm: num(v.length_cm, DEFAULT_PALLET_VOLUMETRIC.length_cm),
            width_cm: num(v.width_cm, DEFAULT_PALLET_VOLUMETRIC.width_cm),
            divisor: num(v.divisor, DEFAULT_PALLET_VOLUMETRIC.divisor),
        },
        size_height_cm,
    };
};

interface WarehouseSettings {
    warehouse: string;
    country?: string;
    // When set, the warehouse has been hidden: it drops out of GET
    // /settings (and therefore out of every warehouse picker, filter and
    // per-warehouse settings table in the Portal). Its data and its
    // users are untouched. Only the Warehouses section below asks for
    // hidden rows, so it can list them and offer to un-hide.
    hidden_at?: string | null;
    allow_duplicate_barcodes: boolean;
    book_in_po_timers: Record<string, number>;
    scan_sheet_po_timers: Record<string, number>;
    bf_sales_window_days: number | string | null;
    bf_hot_cover_days: number | string | null;
    bf_big_max_length_cm: number | string | null;
    bf_new_sku_hot_units: number | string | null;
    bf_tote_max_weight_kg: number | string | null;
    sorter_2_location_timers: Record<string, number>;
    sorter_1_timer_ms: number | null;
    put_away_timer_ms: number | null;
    home_modules_visible: HomeModuleVisibilityMap;
    send_to_ppd: boolean;
    // Zone Storage → Portal dispatch instead of PPd, per warehouse. Sorter 2
    // close-tag (BE migration 225) and the Sorter 1 singles lane (231) switch
    // independently. Both live under Settings › Sorting › Portal dispatch.
    zone_dispatch_via_portal: boolean;
    zone_singles_dispatch_via_portal: boolean;
    // Unsold Reauction → Portal stock move (BE migration 237): Sorter 1
    // reauction tote + conveyor reauction pallet move stock in Portal, no PPd.
    zone_reauction_via_portal: boolean;
    // Unsold Grey → Portal write-off (BE migration 247): confirming a Sorter 1
    // slot for a grey item writes one unit of the SKU off Portal stock.
    zone_grey_write_off_via_portal: boolean;
    // Allocate stock the moment an order lands, hidden from the pick queues
    // (BE migration 233). Same Portal dispatch section.
    allocate_on_ingest: boolean;
    // Auto release-short sweep for partly stock-blocked orders (BE
    // migration 240). Lives under Settings › Picking › Wave building.
    auto_release_short: boolean;
    allow_item_level_put_away: boolean;
    allow_out_of_tote_put_away: boolean;
    allow_multi_user_book_in: boolean;
    allow_tote_reopen: boolean;
    bulk_qty_book_in: boolean;
    manifest_required_book_in: boolean;
    // Master switch for the per-supplier "Allow duplicates" flags
    // (migration 245): when on, a scan whose SKU already has stock at an
    // in-transit / sellable location here is rejected for suppliers with
    // the flag off. Red and yellow totes are never checked.
    book_in_duplicate_stock_check: boolean;
    dispatch_show_images: boolean;
    dispatch_box_recommendation_enabled: boolean;
    // When TRUE the box recommender only suggests boxes with
    // current_stock > 0. OFF by default — warehouses that don't maintain
    // box stock counts keep every configured box eligible.
    dispatch_stock_aware_recommendation: boolean;
    // Diagonal placement: long items may be recommended a smaller
    // container they fit rotated-in-plane (ON by default), with their
    // own — larger — packing tolerances (boxes+cardboard mailers vs
    // plastic mailbags). NULL tolerance → FE default of 10mm.
    dispatch_diagonal_recommendation: boolean;
    dispatch_diagonal_tolerance_mm: number | string | null;
    dispatch_diagonal_bag_tolerance_mm: number | string | null;
    // Dispatch-box NAMES a product flagged "extra protection needed" may
    // not travel in. Empty = nothing excluded. Replaces the old hardcoded
    // "never a plastic mailbag" rule (migration 131 seeded each
    // warehouse's plastic mailbags, so behaviour started out unchanged).
    dispatch_extra_protection_excluded_boxes: string[];
    // Goods In > Inbound: what an inbound label can represent. Code is
    // the barcode prefix, name is printed on the label (migration 188).
    inbound_stock_types?: InboundStockType[];
    // Inbound dashboard age thresholds (days). NULL = off.
    inbound_pallet_age_warn_days?: number | string | null;
    inbound_pallet_age_alert_days?: number | string | null;
    // Goods In > Pallets: how a sale pallet label is built (migration 191).
    // Stored partial — resolvePalletSettings() applies the defaults.
    pallet_settings?: PalletSettingsStored;
    // Outbound /shipments release scheduling (see Settings > Dispatch).
    // 'immediate'  — push to partner within ~30s of pack-complete.
    // 'cutoff'     — hold the push until release_time wall-clock in
    //                the configured timezone.
    dispatch_shipments_release_mode: "immediate" | "cutoff";
    // What a channel PALLET order (every line a pallet SKU) is born as at
    // this warehouse: straight to Pallet Picks, or held until released.
    pallet_order_status: "unfulfilled" | "on_hold";
    dispatch_shipments_release_time: string;     // "HH:MM:SS"
    dispatch_shipments_release_timezone: string; // IANA name e.g. "Europe/London"
    // Immediate-mode grace delay (minutes, 0–60 in 5s, null → 15) before
    // the partner shipment push goes out — the reopen window.
    dispatch_shipments_immediate_delay_mins: number | string | null;
    dispatch_void_fill_pct: number | string | null;
    dispatch_packing_tolerance_mm: number | string | null;
    picking_max_wave_orders: number | string | null;
    // Double-bay Large P&S waves: per-warehouse feature toggle + the
    // wave-order ceiling (actual capacity = both bays' enabled slots).
    picking_pas_large_wave_enabled: boolean;
    picking_pas_large_wave_orders: number | string | null;
    // Per-order eligibility for Large waves (0/null = no limit):
    // over-limit orders stay P&S but build into Small waves.
    picking_pas_large_max_items: number | string | null;
    picking_pas_large_max_order_kg: number | string | null;
    // Put wall (Pembroke): a Pick & Sort wave variant that takes only
    // orders fitting whole into one tote (sort_station = 'put_wall').
    // The tote dims x fill % decide "fits"; min_orders drives the
    // selector's "Include put wall orders" default.
    picking_put_wall_enabled: boolean;
    picking_put_wall_wave_orders: number | string | null;
    picking_put_wall_tote_l_cm: number | string | null;
    picking_put_wall_tote_w_cm: number | string | null;
    picking_put_wall_tote_h_cm: number | string | null;
    picking_put_wall_fill_pct: number | string | null;
    picking_put_wall_min_orders: number | string | null;
    // Totes (= neighbouring cubbies) an order may need and still go to the
    // Putwall. 1 = fits one tote; Pembroke runs 2.
    picking_put_wall_max_totes: number | string | null;
    // Paired Pick & Sort waves (migration 223): two pickers on one wave,
    // each on their own aisles. The toggle, segment 1's share of the
    // lines, and whether the bay may start on the first picked half.
    picking_ps_pairing_enabled: boolean;
    picking_ps_segment_split_pct: number | string | null;
    picking_ps_sort_on_first_segment: boolean;
    // A pair is only built above this many units (Alex, 2026-10-19: 100).
    picking_ps_pair_min_units: number | string | null;
    picking_light_max_wave_orders: number | string | null;
    picking_anchor_count: number | string | null;
    picking_auto_pause_minutes: number | string | null;
    picking_lookahead_orders: number | string | null;
    picking_large_max_items: number | string | null;
    picking_large_max_order_kg: number | string | null;
    picking_large_max_order_cbm: number | string | null;
    picking_large_max_item_grams: number | string | null;
    picking_large_max_item_l_cm: number | string | null;
    picking_large_max_item_w_cm: number | string | null;
    picking_large_max_item_h_cm: number | string | null;
    picking_roll_cage_cbm: number | string | null;
    picking_roll_cage_utilisation_pct: number | string | null;
    picking_require_location_scan: boolean;
    picking_local_first_enabled: boolean;
    // Location-commonality wave grouping (Pick & Sort) — fill waves with
    // orders that share boxes so the picker visits fewer locations.
    picking_location_grouping_enabled: boolean;
    // Unified picking-screen redesign settings (054 migration).
    // Drives whether the picker hero renders the product image and
    // how many "Also at this location" siblings the picker sees.
    picking_show_product_image: boolean;
    picking_other_skus_count: number | string | null;
    picking_queue_1_name: string;
    picking_queue_2_name: string;
    picking_queue_3_name: string;
    // How long the full-screen location-change takeover stays up (ms)
    // when the picker's next line is at a different location.
    picking_location_change_ms: number | null;
    // How long the post-dismiss "new location" highlight holds (ms).
    picking_new_location_highlight_ms: number | null;
    // Walk-pattern A/B experiment (Settings > Picking > Experiments).
    // `picking_walk_pattern` is the order used by waves NOT in the
    // experiment — i.e. what "adopt for everyone" writes once it concludes.
    // Waves in the experiment are assigned an arm at build time and keep it
    // for their whole life, so this never disturbs a wave in flight.
    // An arm code: 'standard', 'corridor', or an uploaded variant's code.
    picking_walk_pattern: string;
    picking_walk_experiment_enabled: boolean;
    picking_walk_experiment_pick_types: PickTypeKey[];
    // The arms enrolled in the experiment, in rotation order.
    picking_walk_experiment_variants: string[];
    picking_walk_experiment_started_at: string | null;
    picking_walk_experiment_ends_at: string | null;
    forecast_drop_lookback_days: number | string | null;
    forecast_drop_target_days_of_stock: number | string | null;
    drop_claim_ttl_minutes: number | string | null;
    require_location_scan_shelf_drop: boolean;
    require_location_scan_forecast_drop: boolean;
    wipe_threshold: number | string | null;
    wipe_dispose_threshold: number | string | null;
    wipe_box_threshold: number | string | null;
    wipe_label_width_mm: number | string | null;
    wipe_label_height_mm: number | string | null;
    wipe_label_orientation: "normal" | "cw" | "ccw" | "flip";
    wipe_auto_process_no: boolean;
    updated_at?: string;
}

// Little "i" beside a column heading. Hover or keyboard focus shows the
// hint as a real tooltip (the heading's title attribute only appears after
// the browser's delay and gives no visual cue that help exists). Used where
// two toggles are easy to confuse — the two book-in duplicate settings.
const HeadInfo = ({ text }: { text: string }) => (
    <span className="admin-settings-head-info" tabIndex={0} aria-label={text}>
        <Info size={13} strokeWidth={2.2} aria-hidden="true" />
        <span role="tooltip" className="admin-settings-head-tip">{text}</span>
    </span>
);

type WarehouseToggleField = "allow_duplicate_barcodes" | "send_to_ppd" | "zone_dispatch_via_portal" | "zone_singles_dispatch_via_portal" | "zone_reauction_via_portal" | "zone_grey_write_off_via_portal" | "allocate_on_ingest" | "auto_release_short" | "allow_item_level_put_away" | "allow_out_of_tote_put_away" | "allow_multi_user_book_in" | "allow_tote_reopen" | "bulk_qty_book_in" | "manifest_required_book_in" | "book_in_duplicate_stock_check" | "dispatch_show_images" | "dispatch_box_recommendation_enabled" | "dispatch_stock_aware_recommendation" | "dispatch_diagonal_recommendation" | "picking_require_location_scan" | "picking_local_first_enabled" | "picking_location_grouping_enabled" | "picking_pas_large_wave_enabled" | "picking_put_wall_enabled" | "picking_ps_pairing_enabled" | "picking_ps_sort_on_first_segment" | "picking_show_product_image" | "require_location_scan_shelf_drop" | "require_location_scan_forecast_drop" | "picking_walk_experiment_enabled" | "wipe_auto_process_no";
type ScalarNumField = "dispatch_void_fill_pct" | "dispatch_packing_tolerance_mm" | "dispatch_diagonal_tolerance_mm" | "dispatch_diagonal_bag_tolerance_mm" | "picking_max_wave_orders" | "picking_pas_large_wave_orders" | "picking_pas_large_max_items" | "picking_pas_large_max_order_kg" | "picking_put_wall_wave_orders" | "picking_put_wall_tote_l_cm" | "picking_put_wall_tote_w_cm" | "picking_put_wall_tote_h_cm" | "picking_put_wall_fill_pct" | "picking_put_wall_min_orders" | "picking_put_wall_max_totes" | "picking_ps_segment_split_pct" | "picking_ps_pair_min_units" | "picking_light_max_wave_orders" | "picking_anchor_count" | "picking_auto_pause_minutes" | "picking_lookahead_orders" | "picking_other_skus_count" | "picking_large_max_items" | "picking_large_max_order_kg" | "picking_large_max_order_cbm" | "picking_large_max_item_grams" | "picking_large_max_item_l_cm" | "picking_large_max_item_w_cm" | "picking_large_max_item_h_cm" | "picking_roll_cage_cbm" | "picking_roll_cage_utilisation_pct" | "forecast_drop_lookback_days" | "forecast_drop_target_days_of_stock" | "drop_claim_ttl_minutes" | "wipe_threshold" | "wipe_dispose_threshold" | "wipe_box_threshold" | "wipe_label_width_mm" | "wipe_label_height_mm" | "bf_hot_cover_days" | "bf_big_max_length_cm" | "bf_new_sku_hot_units" | "bf_tote_max_weight_kg" | "inbound_pallet_age_warn_days" | "inbound_pallet_age_alert_days";
type PickingQueueNameField = "picking_queue_1_name" | "picking_queue_2_name" | "picking_queue_3_name";

// Per-warehouse non-timer numeric scalar (e.g. void fill % or packing
// tolerance mm). Same shape as ScalarTimerEditor but with configurable
// unit/step/placeholder so we can reuse it for both dispatch settings.
const ScalarNumEditor = ({
    rows,
    field,
    placeholder,
    unit,
    step,
    max,
    onSaved,
}: {
    rows: WarehouseSettings[];
    field: ScalarNumField;
    placeholder: number;
    unit: string;
    step: number;
    max: number;
    onSaved: (warehouse: string, next: number | null) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const buildBuffer = (src: WarehouseSettings[]): Record<string, string> => {
        const out: Record<string, string> = {};
        src.forEach(r => {
            const v = r[field];
            // BE returns NUMERIC as string ("15.00") and INTEGER as number — normalise.
            out[r.warehouse] = v == null || v === "" ? "" : String(Number(v));
        });
        return out;
    };

    const [buffer, setBuffer] = useState<Record<string, string>>(() => buildBuffer(rows));
    const [savingFor, setSavingFor] = useState<string | null>(null);

    useEffect(() => {
        setBuffer(buildBuffer(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const isDirty = (warehouse: string) => {
        const row = rows.find(r => r.warehouse === warehouse);
        if (!row) return false;
        const raw = buffer[warehouse] ?? "";
        const savedRaw = row[field];
        const saved = savedRaw == null || savedRaw === "" ? null : Number(savedRaw);
        if (raw === "") return saved != null;
        const n = Number(raw);
        if (!Number.isFinite(n)) return false;
        return n !== saved;
    };

    const save = async (warehouse: string) => {
        const raw = buffer[warehouse] ?? "";
        let payload: number | null;
        if (raw === "") {
            payload = null;
        } else {
            const n = Number(raw);
            if (!Number.isFinite(n) || n < 0) return;
            payload = Math.min(Math.round(n * 100) / 100, max);
        }

        setSavingFor(warehouse);
        try {
            const res = await apiFetch(
                `/settings/${encodeURIComponent(warehouse)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ [field]: payload }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            const v = updated[field];
            onSaved(warehouse, v == null || v === "" ? null : Number(v));
        } catch {
            toast.error(t("server_error"), { toastId: `settings-${field}-save-err-${warehouse}` });
        } finally {
            setSavingFor(null);
        }
    };

    const saveIfDirty = (warehouse: string) => {
        if (savingFor === warehouse) return;
        if (!isDirty(warehouse)) return;
        save(warehouse);
    };
    const autosave = useDebouncedAutoSave(saveIfDirty);

    return (
        <div className="admin-settings-table">
            {rows.map(r => (
                <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                    <div className="admin-settings-warehouse">{r.warehouse}</div>
                    <div className="admin-settings-scalar-control">
                        <input
                            className="admin-settings-po-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={max}
                            step={step}
                            placeholder={String(placeholder)}
                            value={buffer[r.warehouse] ?? ""}
                            onChange={e => {
                                setBuffer(prev => ({ ...prev, [r.warehouse]: e.target.value }));
                                autosave.schedule(r.warehouse);
                            }}
                            onBlur={() => autosave.flush(r.warehouse)}
                            disabled={savingFor === r.warehouse || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        />
                        <span className="admin-settings-po-unit">
                            {savingFor === r.warehouse ? t("saving") : unit}
                        </span>
                    </div>
                </div>
            ))}
            {rows.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}
        </div>
    );
};

const PICKING_QUEUE_NAME_FIELDS: PickingQueueNameField[] = [
    "picking_queue_1_name",
    "picking_queue_2_name",
    "picking_queue_3_name",
];

const PickingQueueNamesEditor = ({
    rows,
    pendingKey,
    onSave,
}: {
    rows: WarehouseSettings[];
    pendingKey: string | null;
    onSave: (warehouse: string, field: PickingQueueNameField, value: string) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const buildBuffer = (source: WarehouseSettings[]) => Object.fromEntries(
        source.flatMap(row => PICKING_QUEUE_NAME_FIELDS.map(field => [
            `${row.warehouse}:${field}`,
            row[field] ?? "",
        ])),
    ) as Record<string, string>;
    const [buffer, setBuffer] = useState<Record<string, string>>(() => buildBuffer(rows));

    useEffect(() => {
        setBuffer(buildBuffer(rows));
    }, [rows]);

    const finishEdit = (row: WarehouseSettings, field: PickingQueueNameField) => {
        const key = `${row.warehouse}:${field}`;
        const saved = row[field] ?? "";
        const trimmed = (buffer[key] ?? "").trim();
        if (!trimmed) {
            setBuffer(prev => ({ ...prev, [key]: saved }));
            return;
        }
        setBuffer(prev => ({ ...prev, [key]: trimmed }));
        if (trimmed !== saved) onSave(row.warehouse, field, trimmed);
    };

    return (
        <div className="admin-settings-table admin-settings-table--queue-names">
            {rows.map(row => (
                <div key={row.warehouse} className="admin-settings-row admin-settings-row--queue-names">
                    <div className="admin-settings-warehouse">{row.warehouse}</div>
                    {PICKING_QUEUE_NAME_FIELDS.map((field, index) => {
                        const key = `${row.warehouse}:${field}`;
                        return (
                            <label key={field} className="admin-settings-queue-name-field">
                                <span>{t(`settings_picking_queue_${index + 1}`)}</span>
                                <input
                                    className="admin-settings-po-input admin-settings-queue-name-input"
                                    type="text"
                                    maxLength={40}
                                    value={buffer[key] ?? ""}
                                    disabled={pendingKey === key || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                    onChange={event => setBuffer(prev => ({ ...prev, [key]: event.target.value }))}
                                    onBlur={() => finishEdit(row, field)}
                                    onKeyDown={event => {
                                        if (event.key === "Enter") event.currentTarget.blur();
                                    }}
                                />
                            </label>
                        );
                    })}
                </div>
            ))}
            {rows.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}
        </div>
    );
};

// Per-warehouse picker for dispatch_extra_protection_excluded_boxes: the
// boxes a product flagged "extra protection needed" must NOT be
// recommended. Chips mirror Settings > Dispatch > Shipping Rules >
// Allowed box types — same control, opposite polarity (selected = ruled
// out), which the section hint spells out.
//
// Box names come from each warehouse's own dispatch-box range, fetched
// per warehouse. A warehouse whose range failed to load (or is empty)
// renders a placeholder rather than an empty row, so an admin never
// mistakes "couldn't load" for "nothing to exclude".
const ExtraProtectionBoxesEditor = ({
    rows,
    onSaved,
}: {
    rows: WarehouseSettings[];
    onSaved: (warehouse: string, next: string[]) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [boxNames, setBoxNames] = useState<Record<string, string[]>>({});
    const [savingFor, setSavingFor] = useState<string | null>(null);

    const warehouseKey = rows.map(r => r.warehouse).join(" ");
    useEffect(() => {
        let alive = true;
        (async () => {
            const entries = await Promise.all(rows.map(async r => {
                try {
                    const res = await apiFetch(
                        `/dispatch-admin/boxes?warehouse=${encodeURIComponent(r.warehouse)}`,
                        {},
                    );
                    if (!res.ok) return [r.warehouse, []] as const;
                    const data: { boxes?: Array<{ name?: string }> } = await res.json();
                    const names = (data.boxes ?? [])
                        .map(b => String(b.name ?? "").trim())
                        .filter(n => n !== "");
                    return [r.warehouse, names] as const;
                } catch {
                    return [r.warehouse, []] as const;
                }
            }));
            if (alive) setBoxNames(Object.fromEntries(entries));
        })();
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [warehouseKey]);

    const save = async (warehouse: string, next: string[]) => {
        setSavingFor(warehouse);
        try {
            const res = await apiFetch(
                `/settings/${encodeURIComponent(warehouse)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ dispatch_extra_protection_excluded_boxes: next }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            onSaved(warehouse, Array.isArray(updated.dispatch_extra_protection_excluded_boxes)
                ? updated.dispatch_extra_protection_excluded_boxes
                : []);
        } catch {
            toast.error(t("server_error"), { toastId: `settings-ep-boxes-err-${warehouse}` });
        } finally {
            setSavingFor(null);
        }
    };

    return (
        <div className="admin-settings-table">
            {rows.map(r => {
                const selected = r.dispatch_extra_protection_excluded_boxes ?? [];
                const names = boxNames[r.warehouse];
                return (
                    <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                        <div className="admin-settings-warehouse">{r.warehouse}</div>
                        <div className="admin-integrations-day-chips shipping-rule-region-chips">
                            {(names ?? []).map(name => (
                                <button
                                    key={name}
                                    type="button"
                                    className={`admin-integrations-day-chip${selected.includes(name) ? " admin-integrations-day-chip--active" : ""}`}
                                    disabled={savingFor === r.warehouse || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                    onClick={() => save(
                                        r.warehouse,
                                        selected.includes(name)
                                            ? selected.filter(n => n !== name)
                                            : [...selected, name],
                                    )}
                                >
                                    {name}
                                </button>
                            ))}
                            {names !== undefined && names.length === 0 && (
                                <span className="admin-settings-po-unit">{t("dispatch_extra_protection_excluded_boxes_none")}</span>
                            )}
                        </div>
                    </div>
                );
            })}
            {rows.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}
        </div>
    );
};


// Goods In > Inbound > Stock types. Per-warehouse list of { name, code }
// the Inbound page prompts from when printing labels. Chips are removable;
// the inline form adds one. Validation mirrors the BE sanitiser (code =
// 1-4 uppercase letters/digits, name ≤ 32 chars, both unique per
// warehouse) so a rejected entry is explained here rather than silently
// dropped on save.
const STOCK_TYPE_CODE_RE = /^[A-Z0-9]{1,4}$/;

const InboundStockTypesEditor = ({
    rows,
    onSaved,
}: {
    rows: WarehouseSettings[];
    onSaved: (warehouse: string, next: InboundStockType[]) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [savingFor, setSavingFor] = useState<string | null>(null);
    const [drafts, setDrafts] = useState<Record<string, { name: string; code: string }>>({});
    const [errors, setErrors] = useState<Record<string, string | null>>({});

    const draftFor = (warehouse: string) => drafts[warehouse] ?? { name: "", code: "" };
    const setDraft = (warehouse: string, patch: Partial<{ name: string; code: string }>) =>
        setDrafts(curr => ({ ...curr, [warehouse]: { ...draftFor(warehouse), ...patch } }));

    const save = async (warehouse: string, next: InboundStockType[]) => {
        setSavingFor(warehouse);
        try {
            const res = await apiFetch(
                `/settings/${encodeURIComponent(warehouse)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ inbound_stock_types: next }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            onSaved(warehouse, Array.isArray(updated.inbound_stock_types)
                ? updated.inbound_stock_types
                : []);
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: `settings-stock-types-err-${warehouse}` });
            return false;
        } finally {
            setSavingFor(null);
        }
    };

    const add = async (warehouse: string, existing: InboundStockType[]) => {
        const draft = draftFor(warehouse);
        const name = draft.name.trim();
        const code = draft.code.trim().toUpperCase();
        if (!name || name.length > 32) return;
        if (!STOCK_TYPE_CODE_RE.test(code)) {
            setErrors(curr => ({ ...curr, [warehouse]: t("inbound_stock_type_code_invalid") }));
            return;
        }
        const dup = existing.some(x =>
            x.name.toUpperCase() === name.toUpperCase() || x.code.toUpperCase() === code);
        if (dup) {
            setErrors(curr => ({ ...curr, [warehouse]: t("inbound_stock_type_duplicate") }));
            return;
        }
        setErrors(curr => ({ ...curr, [warehouse]: null }));
        const ok = await save(warehouse, [...existing, { name, code }]);
        if (ok) setDrafts(curr => ({ ...curr, [warehouse]: { name: "", code: "" } }));
    };

    return (
        <div className="admin-settings-table">
            {rows.map(r => {
                const types = r.inbound_stock_types ?? [];
                const draft = draftFor(r.warehouse);
                const busy = savingFor === r.warehouse || readOnly;
                const error = errors[r.warehouse];
                return (
                    <div key={r.warehouse} className="admin-settings-row admin-settings-row--stock-types">
                        <div className="admin-settings-warehouse">{r.warehouse}</div>
                        <div className="admin-settings-stock-types">
                            <div className="admin-settings-stock-type-chips">
                                {types.map(x => (
                                    <span key={x.code} className="admin-settings-stock-type-chip">
                                        {x.name}
                                        <code>{x.code}</code>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            aria-label={t("inbound_stock_type_remove", { name: x.name })}
                                            title={readOnly ? t("read_only_mode") : t("inbound_stock_type_remove", { name: x.name })}
                                            onClick={() => save(r.warehouse, types.filter(y => y.code !== x.code))}
                                        >
                                            <X size={14} />
                                        </button>
                                    </span>
                                ))}
                                {types.length === 0 && (
                                    <span className="admin-settings-po-unit">{t("inbound_stock_types_none")}</span>
                                )}
                            </div>
                            <form
                                className="admin-settings-stock-type-add"
                                onSubmit={e => { e.preventDefault(); add(r.warehouse, types); }}
                            >
                                <input
                                    name="name"
                                    value={draft.name}
                                    maxLength={32}
                                    placeholder={t("inbound_stock_type_name_placeholder")}
                                    aria-label={t("inbound_stock_type_name")}
                                    disabled={busy}
                                    onChange={e => setDraft(r.warehouse, { name: e.target.value })}
                                />
                                <input
                                    name="code"
                                    value={draft.code}
                                    maxLength={4}
                                    placeholder={t("inbound_stock_type_code_placeholder")}
                                    aria-label={t("inbound_stock_type_code")}
                                    disabled={busy}
                                    onChange={e => setDraft(r.warehouse, { code: e.target.value.toUpperCase() })}
                                />
                                <button
                                    type="submit"
                                    disabled={busy || !draft.name.trim() || !draft.code.trim()}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    <Plus size={14} />
                                    {t("inbound_stock_type_add")}
                                </button>
                                {error && <span className="admin-settings-stock-type-error">{error}</span>}
                            </form>
                        </div>
                    </div>
                );
            })}
            {rows.length === 0 && (
                <div className="admin-settings-empty">{t("no_warehouses")}</div>
            )}
        </div>
    );
};

// One row in the home-modules role-toggle table. Used for both parents
// (with an optional expand chevron) and indented children. Renders a
// label cell on the left and three role toggles to the right; sharing a
// single component keeps the grid columns aligned.
const RoleModuleRow = ({
    label,
    indent,
    expandable,
    expanded,
    onToggleExpand,
    warehouse,
    moduleKey,
    map,
    pendingKey,
    onToggle,
}: {
    label: string;
    indent: boolean;
    expandable: boolean;
    expanded: boolean;
    onToggleExpand: () => void;
    warehouse: string;
    moduleKey: string;
    map: HomeModuleVisibilityMap | undefined;
    pendingKey: string | null;
    onToggle: (warehouse: string, moduleKey: string, role: Role, next: boolean) => void;
}) => {
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    return (
    <div className={`admin-settings-row admin-settings-row--module${indent ? " admin-settings-row--module-child" : ""}`}>
        <div className="admin-settings-module-label">
            {expandable ? (
                <button
                    type="button"
                    className={`admin-settings-module-chevron${expanded ? " admin-settings-module-chevron--open" : ""}`}
                    onClick={onToggleExpand}
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    <ChevronRight size={16} />
                </button>
            ) : (
                <span className="admin-settings-module-chevron-placeholder" />
            )}
            <span>{label}</span>
        </div>
        {ROLES.map(role => {
            const checked = isVisible(map, moduleKey, role);
            const pending = pendingKey === `${warehouse}:home_module:${moduleKey}:${role}`;
            return (
                <div key={role}>
                    <label className="admin-settings-toggle">
                        <input
                            type="checkbox"
                            checked={checked}
                            disabled={pending || readOnly}
                            onChange={e => onToggle(warehouse, moduleKey, role, e.target.checked)}
                        />
                        <span className="admin-settings-slider" />
                    </label>
                </div>
            );
        })}
    </div>
    );
};

const RoleModuleParent = ({
    warehouse,
    map,
    pendingKey,
    parent,
    onToggle,
}: {
    warehouse: string;
    map: HomeModuleVisibilityMap | undefined;
    pendingKey: string | null;
    parent: typeof HOME_MODULES[number];
    onToggle: (warehouse: string, moduleKey: string, role: Role, next: boolean) => void;
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const hasChildren = parent.children.length > 0;
    return (
        <>
            <RoleModuleRow
                label={t(parent.labelKey)}
                indent={false}
                expandable={hasChildren}
                expanded={expanded}
                onToggleExpand={() => setExpanded(v => !v)}
                warehouse={warehouse}
                moduleKey={parent.key}
                map={map}
                pendingKey={pendingKey}
                onToggle={onToggle}
            />
            {expanded && parent.children.map(child => (
                <RoleModuleRow
                    key={child.key}
                    label={t(child.labelKey)}
                    indent
                    expandable={false}
                    expanded={false}
                    onToggleExpand={() => {}}
                    warehouse={warehouse}
                    moduleKey={child.key}
                    map={map}
                    pendingKey={pendingKey}
                    onToggle={onToggle}
                />
            ))}
        </>
    );
};

const PalletSettingsEditor = ({
    rows,
    onSaved,
}: {
    rows: WarehouseSettings[];
    onSaved: (warehouse: string, next: PalletSettingsStored) => void;
}) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [savingFor, setSavingFor] = useState<string | null>(null);
    // Per-warehouse unsaved input: the add-name boxes, the next-number
    // fields, the volumetric formula numbers and the per-size heights.
    type VolKey = "length_cm" | "width_cm" | "divisor";
    type Draft = { type: string; size: string; seq: Partial<Record<PalletColourKey, string>>; vol: Partial<Record<VolKey, string>>; heights: Record<string, string> };
    const VOL_KEYS: readonly VolKey[] = ["length_cm", "width_cm", "divisor"];
    const [drafts, setDrafts] = useState<Record<string, Draft>>({});
    // Live numbering from the BE: last printed + next to issue, per
    // warehouse + colour. Reloaded after every save so the field always
    // shows the current number, not the value typed at go-live.
    type Numbers = Record<string, Partial<Record<PalletColourKey, { last: number; next: number }>>>;
    const [numbers, setNumbers] = useState<Numbers>({});
    // Which warehouses ship pallets by Palletways (they have a Palletways
    // carrier account) and the valid pallet type codes. Only those sites
    // get the per-size Palletways type.
    const [palletways, setPalletways] = useState<{ warehouses: string[]; types: string[] }>({ warehouses: [], types: [] });
    const loadNumbers = async () => {
        try {
            const res = await apiFetch(`/pallets/admin/next-numbers`, {});
            if (!res.ok) return;
            const data = await res.json();
            if (data && typeof data.warehouses === "object") setNumbers(data.warehouses);
            if (Array.isArray(data?.palletways?.warehouses) && Array.isArray(data?.palletways?.types)) {
                setPalletways({ warehouses: data.palletways.warehouses, types: data.palletways.types });
            }
        } catch { /* keep whatever we had */ }
    };
    useEffect(() => { loadNumbers(); }, []);

    const draftFor = (warehouse: string): Draft => drafts[warehouse] ?? { type: "", size: "", seq: {}, vol: {}, heights: {} };
    const setDraft = (warehouse: string, patch: Partial<Draft>) =>
        setDrafts(curr => ({ ...curr, [warehouse]: { ...draftFor(warehouse), ...patch } }));

    const save = async (warehouse: string, patch: PalletSettingsStored) => {
        setSavingFor(warehouse);
        try {
            const res = await apiFetch(
                `/settings/${encodeURIComponent(warehouse)}`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ pallet_settings: patch }),
                },
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            onSaved(warehouse, updated.pallet_settings ?? {});
            toast.success(t("pallets_settings_saved"), { toastId: `settings-pallets-${warehouse}` });
            loadNumbers();
            return true;
        } catch {
            toast.error(t("server_error"), { toastId: `settings-pallets-err-${warehouse}` });
            return false;
        } finally {
            setSavingFor(null);
        }
    };

    const addName = async (warehouse: string, key: "types" | "sizes", existing: string[], raw: string) => {
        const name = raw.trim();
        if (!name || name.length > 32) return;
        if (existing.some(x => x.toUpperCase() === name.toUpperCase())) return;
        const ok = await save(warehouse, { [key]: [...existing, name] });
        if (ok) setDraft(warehouse, key === "types" ? { type: "" } : { size: "" });
    };

    const chipList = (warehouse: string, key: "types" | "sizes", list: string[], busy: boolean, emptyKey: string, inputName: string) => (
        <div className="admin-settings-stock-types">
            <div className="admin-settings-stock-type-chips">
                {list.map(name => (
                    <span key={name} className="admin-settings-stock-type-chip">
                        {name}
                        <button
                            type="button"
                            disabled={busy}
                            aria-label={t("pallets_settings_remove", { name })}
                            title={readOnly ? t("read_only_mode") : t("pallets_settings_remove", { name })}
                            onClick={() => save(warehouse, { [key]: list.filter(x => x !== name) })}
                        >
                            <X size={14} />
                        </button>
                    </span>
                ))}
                {list.length === 0 && <span className="admin-settings-po-unit">{t(emptyKey)}</span>}
            </div>
            <form
                className="admin-settings-stock-type-add"
                onSubmit={e => { e.preventDefault(); addName(warehouse, key, list, key === "types" ? draftFor(warehouse).type : draftFor(warehouse).size); }}
            >
                <input
                    name={inputName}
                    value={key === "types" ? draftFor(warehouse).type : draftFor(warehouse).size}
                    maxLength={32}
                    placeholder={t("pallets_settings_name_placeholder")}
                    aria-label={`${t(key === "types" ? "pallets_settings_types" : "pallets_settings_sizes")} ${warehouse}`}
                    disabled={busy}
                    onChange={e => setDraft(warehouse, key === "types" ? { type: e.target.value } : { size: e.target.value })}
                />
                <button type="submit" disabled={busy} title={readOnly ? t("read_only_mode") : undefined}>{t("pallets_settings_add")}</button>
            </form>
        </div>
    );

    return (
        <div className="admin-settings-table">
            {rows.map(r => {
                const s = resolvePalletSettings(r.pallet_settings);
                const busy = savingFor === r.warehouse || readOnly;
                const draft = draftFor(r.warehouse);
                const nums = numbers[r.warehouse] ?? {};
                const lastOf = (c: PalletColourKey) => nums[c]?.last ?? 0;
                const shown = (c: PalletColourKey) => nums[c]?.next != null
                    ? String(nums[c]!.next)
                    : (s.next_seq[c] != null ? String(s.next_seq[c]) : "");
                const seqValue = (c: PalletColourKey) => draft.seq[c] ?? shown(c);
                const seqDirty = PALLET_COLOUR_KEYS.some(c => draft.seq[c] !== undefined && draft.seq[c] !== shown(c));
                const saveSeq = async () => {
                    const next: Partial<Record<PalletColourKey, number>> = {};
                    for (const c of PALLET_COLOUR_KEYS) {
                        const v = seqValue(c).trim();
                        if (v === "") continue;
                        const n = Math.floor(Number(v));
                        if (!Number.isFinite(n) || n < 1) continue;
                        if (n <= lastOf(c)) {
                            toast.error(t("pallets_settings_seq_too_low", { colour: t(`colour_${c.toLowerCase()}`), last: String(lastOf(c)) }), { toastId: `settings-pallets-seq-${r.warehouse}` });
                            return;
                        }
                        next[c] = n;
                    }
                    const ok = await save(r.warehouse, { next_seq: next });
                    if (ok) setDraft(r.warehouse, { seq: {} });
                };
                // Volumetric formula numbers: typed values over the resolved
                // ones; Save sends the whole object so nothing stored is lost.
                const volValue = (k: VolKey) => draft.vol[k] ?? String(s.volumetric[k]);
                const volDirty = VOL_KEYS.some(k => draft.vol[k] !== undefined && draft.vol[k] !== String(s.volumetric[k]));
                const saveVol = async () => {
                    const next = { ...s.volumetric };
                    for (const k of VOL_KEYS) {
                        const n = Number(volValue(k));
                        if (!Number.isFinite(n) || n <= 0) {
                            toast.error(t("pallets_settings_volumetric_invalid"), { toastId: `settings-pallets-vol-${r.warehouse}` });
                            return;
                        }
                        next[k] = n;
                    }
                    const ok = await save(r.warehouse, { volumetric: next });
                    if (ok) setDraft(r.warehouse, { vol: {} });
                };
                // Per-size heights: the whole map is saved, so a blanked
                // size drops its height.
                const heightValue = (name: string) => draft.heights[name] ?? (s.size_height_cm[name] != null ? String(s.size_height_cm[name]) : "");
                const heightsDirty = s.sizes.some(name => draft.heights[name] !== undefined && draft.heights[name] !== (s.size_height_cm[name] != null ? String(s.size_height_cm[name]) : ""));
                const saveHeights = async () => {
                    const next: Record<string, number> = {};
                    for (const name of s.sizes) {
                        const v = heightValue(name).trim();
                        if (v === "") continue;
                        const n = Number(v);
                        if (!Number.isFinite(n) || n <= 0 || n > 300) {
                            toast.error(t("pallets_settings_size_height_invalid", { name }), { toastId: `settings-pallets-height-${r.warehouse}` });
                            return;
                        }
                        next[name] = n;
                    }
                    const ok = await save(r.warehouse, { size_height_cm: next });
                    if (ok) setDraft(r.warehouse, { heights: {} });
                };
                return (
                    <div key={r.warehouse} className="pallets-settings-row">
                        <div className="admin-settings-warehouse">{r.warehouse}</div>
                        <div className="pallets-settings-groups">
                            <div className="pallets-settings-group">
                                <span className="pallets-settings-group-label">{t("pallets_settings_types")}</span>
                                {chipList(r.warehouse, "types", s.types, busy, "pallets_settings_types_none", "type")}
                            </div>
                            <div className="pallets-settings-group">
                                <span className="pallets-settings-group-label">{t("pallets_settings_sizes")}</span>
                                {chipList(r.warehouse, "sizes", s.sizes, busy, "pallets_settings_sizes_none", "size")}
                            </div>
                            {palletways.warehouses.includes(r.warehouse) && s.sizes.length > 0 && (
                                <div className="pallets-settings-group">
                                    <span className="pallets-settings-group-label" title={t("pallets_settings_palletways_hint")}>
                                        {t("pallets_settings_palletways")}
                                    </span>
                                    {s.sizes.map(name => (
                                        <label key={name} className="pallets-settings-seq">
                                            <span>{name}</span>
                                            <select
                                                value={s.size_palletways[name] ?? ""}
                                                disabled={busy}
                                                aria-label={`${t("pallets_settings_palletways")} ${name} ${r.warehouse}`}
                                                onChange={e => {
                                                    // The whole map is saved each time, starting from the
                                                    // RESOLVED one, so the defaults for the other sizes
                                                    // are stored too rather than lost.
                                                    const next = { ...s.size_palletways };
                                                    if (e.target.value) next[name] = e.target.value;
                                                    else delete next[name];
                                                    save(r.warehouse, { size_palletways: next });
                                                }}
                                            >
                                                <option value="">{t("pallets_settings_palletways_none")}</option>
                                                {palletways.types.map(code => (
                                                    <option key={code} value={code} title={palletwaysTypeTitle(code, t)}>{palletwaysTypeLabel(code, t)}</option>
                                                ))}
                                            </select>
                                        </label>
                                    ))}
                                </div>
                            )}
                            {/* Volumetric weight (Alex, 2026-10-13): Poland is billed on
                                volume, so its pallets weigh footprint × height ÷ divisor. */}
                            <div className="pallets-settings-group">
                                <span className="pallets-settings-group-label" title={t("pallets_settings_volumetric_hint")}>
                                    {t("pallets_settings_volumetric")}
                                </span>
                                <label className="pallets-settings-toggle">
                                    <input
                                        type="checkbox"
                                        checked={s.volumetric.enabled}
                                        disabled={busy}
                                        aria-label={`${t("pallets_settings_volumetric")} ${r.warehouse}`}
                                        onChange={e => save(r.warehouse, { volumetric: { enabled: e.target.checked } })}
                                    />
                                    <span>{t("pallets_settings_volumetric_on")}</span>
                                </label>
                                {s.volumetric.enabled && (
                                    <>
                                        {VOL_KEYS.map(k => (
                                            <label key={k} className="pallets-settings-seq">
                                                <span>{t(`pallets_settings_volumetric_${k}`)}</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    step="any"
                                                    value={volValue(k)}
                                                    disabled={busy}
                                                    aria-label={`${t(`pallets_settings_volumetric_${k}`)} ${r.warehouse}`}
                                                    onChange={e => setDraft(r.warehouse, { vol: { ...draft.vol, [k]: e.target.value } })}
                                                />
                                            </label>
                                        ))}
                                        <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !volDirty} onClick={saveVol}>
                                            {t("save")}
                                        </button>
                                        <small className="admin-settings-po-unit pallets-settings-formula" data-testid={`pallets-volumetric-formula-${r.warehouse}`}>
                                            {t("pallets_settings_volumetric_formula", { l: volValue("length_cm"), w: volValue("width_cm"), d: volValue("divisor") })}
                                        </small>
                                    </>
                                )}
                            </div>
                            {s.volumetric.enabled && s.sizes.length > 0 && (
                                <div className="pallets-settings-group">
                                    <span className="pallets-settings-group-label" title={t("pallets_settings_size_height_hint")}>
                                        {t("pallets_settings_size_height")}
                                    </span>
                                    {s.sizes.map(name => (
                                        <label key={name} className="pallets-settings-seq">
                                            <span>{name}</span>
                                            <input
                                                type="number"
                                                min={1}
                                                max={300}
                                                step="any"
                                                value={heightValue(name)}
                                                placeholder="—"
                                                disabled={busy}
                                                aria-label={`${t("pallets_settings_size_height")} ${name} ${r.warehouse}`}
                                                onChange={e => setDraft(r.warehouse, { heights: { ...draft.heights, [name]: e.target.value } })}
                                            />
                                            <small className="admin-settings-po-unit">cm</small>
                                        </label>
                                    ))}
                                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !heightsDirty} onClick={saveHeights}>
                                        {t("save")}
                                    </button>
                                </div>
                            )}
                            <div className="pallets-settings-group">
                                <span className="pallets-settings-group-label" />
                                <label className="pallets-settings-toggle">
                                    <input
                                        type="checkbox"
                                        checked={s.allow_height}
                                        disabled={busy}
                                        aria-label={`${t("pallets_settings_allow_height")} ${r.warehouse}`}
                                        onChange={e => save(r.warehouse, { allow_height: e.target.checked })}
                                    />
                                    <span>{t("pallets_settings_allow_height")}</span>
                                </label>
                            </div>
                            <div className="pallets-settings-group">
                                <span className="pallets-settings-group-label" title={t("pallets_settings_hazard_flags_hint")}>
                                    {t("pallets_settings_hazard_flags")}
                                </span>
                                <label className="pallets-settings-toggle">
                                    <input
                                        type="checkbox"
                                        checked={s.hazard_flags}
                                        disabled={busy}
                                        aria-label={`${t("pallets_settings_hazard_flags")} ${r.warehouse}`}
                                        onChange={e => save(r.warehouse, { hazard_flags: e.target.checked })}
                                    />
                                    <span>{t("pallets_settings_hazard_flags_on")}</span>
                                </label>
                            </div>
                            <div className="pallets-settings-group">
                                <span className="pallets-settings-group-label">{t("pallets_settings_next_seq")}</span>
                                {PALLET_COLOUR_KEYS.map(c => (
                                    <label key={c} className="pallets-settings-seq">
                                        <span>{t(`colour_${c.toLowerCase()}`)}</span>
                                        <input
                                            type="number"
                                            min={lastOf(c) + 1}
                                            step={1}
                                            value={seqValue(c)}
                                            disabled={busy}
                                            title={t("pallets_settings_last_printed", { n: String(lastOf(c)) })}
                                            aria-label={`${t("pallets_settings_next_seq")} ${c} ${r.warehouse}`}
                                            onChange={e => setDraft(r.warehouse, { seq: { ...draft.seq, [c]: e.target.value } })}
                                        />
                                        <small className="admin-settings-po-unit">{t("pallets_settings_last_printed", { n: String(lastOf(c)) })}</small>
                                    </label>
                                ))}
                                <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !seqDirty} onClick={saveSeq}>
                                    {t("save")}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const AdminSettings = () => {
    const location = useLocation();
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();
    // Without cross_warehouse every per-warehouse table (and the selectors fed from it) shows only
    // the person's own warehouse.
    const { canCross: canCrossWarehouses, own: ownWarehouse } = useWarehouseScope();
    const [allRows, setRows] = useState<WarehouseSettings[]>([]);
    const rows = useMemo(
        () => (canCrossWarehouses ? allRows : allRows.filter(r => r.warehouse === ownWarehouse)),
        [allRows, canCrossWarehouses, ownWarehouse],
    );
    // Hidden warehouses, fetched separately (see the load effect). Only the
    // Warehouses section reads this — everything else on the page uses
    // `rows`, which the BE already filters down to the visible ones.
    const [hiddenRows, setHiddenRows] = useState<WarehouseSettings[]>([]);
    const [loading, setLoading] = useState(true);
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    // Shipments-release cutoff time: in-progress edits per warehouse. Kept
    // OUT of `rows` — the old pattern wrote each keystroke into rows and
    // the blur save then compared the field against that already-updated
    // value, concluded "unchanged", and never saved. `rows` now only ever
    // holds server-confirmed values; the draft overlays it while editing.
    const [releaseTimeDrafts, setReleaseTimeDrafts] = useState<Record<string, string>>({});
    const releaseTimeTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    // Shared Lighting polls light-agents every 10s while mounted. A collapsed
    // <details> still keeps its children mounted, so gate the component on both
    // the Sorting section and the Lighting subsection being open — otherwise it
    // polls the whole time Admin Settings is open.
    const [sortingOpen, setSortingOpen] = useState(false);
    const [sortingLightingOpen, setSortingLightingOpen] = useState(false);
    // Same lazy-mount reason as the Sorting panels above: the Experiments
    // panel polls the results endpoint, and a collapsed <details> would
    // otherwise keep it mounted and polling.
    const [pickingExperimentsOpen, setPickingExperimentsOpen] = useState(false);
    // The layout editor draws every bay in the warehouse, so it waits for its
    // own disclosure as well as the Experiments one around it.
    const [warehouseLayoutOpen, setWarehouseLayoutOpen] = useState(false);
    const demoOn = useDemoMode();
    const demoAudience = useDemoAudience();

    // Add-warehouse modal state. Open via the "Warehouses" section's
    // "+ Add" button; closes on submit or click-outside.
    const [addWarehouseOpen, setAddWarehouseOpen] = useState(false);
    const [addWarehouseName, setAddWarehouseName] = useState("");
    const [addWarehouseCountry, setAddWarehouseCountry] = useState("");
    const [addWarehouseSubmitting, setAddWarehouseSubmitting] = useState(false);
    const closeAddWarehouse = () => {
        setAddWarehouseOpen(false);
        setAddWarehouseName("");
        setAddWarehouseCountry("");
    };

    // Edit-warehouse modal state. Country is straightforward; renaming
    // is allowed but gated on a "type the current name to confirm" step
    // because it cascades across every live-state Postgres table AND
    // every Mongo collection that references the warehouse — get this
    // wrong and references break.
    const [editWarehouse, setEditWarehouse] = useState<WarehouseSettings | null>(null);
    const [editWarehouseName, setEditWarehouseName] = useState("");
    const [editWarehouseCountry, setEditWarehouseCountry] = useState("");
    const [editWarehouseSubmitting, setEditWarehouseSubmitting] = useState(false);
    const [editWarehouseRenameConfirm, setEditWarehouseRenameConfirm] = useState("");
    const openEditWarehouse = (w: WarehouseSettings) => {
        setEditWarehouse(w);
        setEditWarehouseName(w.warehouse);
        setEditWarehouseCountry(w.country ?? "");
        setEditWarehouseRenameConfirm("");
    };
    const closeEditWarehouse = () => {
        setEditWarehouse(null);
        setEditWarehouseName("");
        setEditWarehouseCountry("");
        setEditWarehouseRenameConfirm("");
    };
    const submitEditWarehouse = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editWarehouse || readOnly) return;
        const newName = editWarehouseName.trim();
        const country = editWarehouseCountry.trim();
        if (!newName || !country || editWarehouseSubmitting) return;

        const isRename = newName !== editWarehouse.warehouse;
        const countryChanged = country !== (editWarehouse.country ?? "");

        // Rename is gated on the confirm input matching the original
        // name exactly. The PATCH and rename endpoints can fire in
        // sequence — country first (against the old name), then rename
        // — so a single submit can change both at once.
        if (isRename && editWarehouseRenameConfirm !== editWarehouse.warehouse) {
            toast.error(t("warehouse_rename_confirm_mismatch"));
            return;
        }
        if (!isRename && !countryChanged) {
            closeEditWarehouse();
            return;
        }

        setEditWarehouseSubmitting(true);
        try {
            // Step 1 — country (PATCH against the CURRENT name). Skip
            // if country is unchanged.
            if (countryChanged) {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(editWarehouse.warehouse)}`,
                    {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ country }),
                    },
                );
                if (!res.ok) {
                    toast.error(t("server_error"));
                    return;
                }
            }

            // Step 2 — rename (POST /settings/<old>/rename). Cascades
            // across all live-state Postgres + Mongo references.
            if (isRename) {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(editWarehouse.warehouse)}/rename`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ newName }),
                    },
                );
                if (!res.ok) {
                    if (res.status === 409) {
                        toast.error(t("warehouse_already_exists"));
                    } else {
                        toast.error(t("server_error"));
                    }
                    return;
                }
                const data: WarehouseSettings & { mongoFailures?: string[] } = await res.json();
                setRows(curr => curr.map(r => r.warehouse === editWarehouse.warehouse ? data : r));
                if (Array.isArray(data.mongoFailures) && data.mongoFailures.length > 0) {
                    toast.warning(t("warehouse_renamed_partial", {
                        name: newName,
                        failures: data.mongoFailures.join(", "),
                    }));
                } else {
                    toast.success(t("warehouse_renamed", { from: editWarehouse.warehouse, to: newName }));
                }
            } else if (countryChanged) {
                // Pull the latest row so the local state matches what
                // the server has after the country PATCH.
                const fresh = await apiFetch(
                    `/settings/${encodeURIComponent(editWarehouse.warehouse)}`,
                    {},
                );
                if (fresh.ok) {
                    const updated: WarehouseSettings = await fresh.json();
                    setRows(curr => curr.map(r => r.warehouse === editWarehouse.warehouse ? updated : r));
                }
                toast.success(t("warehouse_updated", { name: editWarehouse.warehouse }));
            }
            closeEditWarehouse();
        } catch {
            toast.error(t("server_error"));
        } finally {
            setEditWarehouseSubmitting(false);
        }
    };
    const submitAddWarehouse = async (e: React.FormEvent) => {
        e.preventDefault();
        const name = addWarehouseName.trim();
        const country = addWarehouseCountry.trim();
        if (!name || !country || addWarehouseSubmitting || readOnly) return;
        setAddWarehouseSubmitting(true);
        try {
            const res = await apiFetch(`/settings/warehouse`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, country }),
            });
            if (res.ok) {
                const created: WarehouseSettings = await res.json();
                setRows(curr => [...curr, created].sort((a, b) => a.warehouse.localeCompare(b.warehouse)));
                toast.success(t("warehouse_added", { name }));
                closeAddWarehouse();
            } else if (res.status === 409) {
                toast.error(t("warehouse_already_exists"));
            } else {
                toast.error(t("server_error"));
            }
        } catch {
            toast.error(t("server_error"));
        } finally {
            setAddWarehouseSubmitting(false);
        }
    };

    // Hide / show a warehouse. One click, no confirmation: it changes
    // nothing but which lists the warehouse appears in, and the same icon
    // puts it straight back. The row moves between `rows` and `hiddenRows`
    // so the rest of the page (which only ever reads `rows`) drops it
    // immediately without a refetch.
    const [visibilityBusy, setVisibilityBusy] = useState<string | null>(null);
    const toggleWarehouseVisibility = async (w: WarehouseSettings) => {
        if (visibilityBusy || readOnly) return;
        const hide = !w.hidden_at;
        setVisibilityBusy(w.warehouse);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(w.warehouse)}/visibility`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ hidden: hide }),
            });
            if (!res.ok) {
                toast.error(t("server_error"));
                return;
            }
            const updated: WarehouseSettings = await res.json();
            if (hide) {
                setRows(curr => curr.filter(r => r.warehouse !== w.warehouse));
                setHiddenRows(curr => [...curr, updated].sort((a, b) => a.warehouse.localeCompare(b.warehouse)));
                toast.success(t("warehouse_hidden", { name: w.warehouse }));
            } else {
                setHiddenRows(curr => curr.filter(r => r.warehouse !== w.warehouse));
                setRows(curr => [...curr, updated].sort((a, b) => a.warehouse.localeCompare(b.warehouse)));
                toast.success(t("warehouse_shown", { name: w.warehouse }));
            }
        } catch {
            toast.error(t("server_error"));
        } finally {
            setVisibilityBusy(null);
        }
    };

    // Delete-warehouse modal state. Opening it fetches the usage report
    // so the dialog can either explain what's blocking the delete or —
    // when nothing references the warehouse — offer the type-to-confirm
    // step. The BE re-checks usage on DELETE, so a warehouse that gains
    // data between opening and confirming is still refused.
    const [deleteWarehouse, setDeleteWarehouse] = useState<WarehouseSettings | null>(null);
    const [deleteUsage, setDeleteUsage] = useState<WarehouseUsage | null>(null);
    const [deleteUsageLoading, setDeleteUsageLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState("");
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const closeDeleteWarehouse = () => {
        if (deleteSubmitting) return;
        setDeleteWarehouse(null);
        setDeleteUsage(null);
        setDeleteConfirm("");
    };
    const openDeleteWarehouse = async (w: WarehouseSettings) => {
        setDeleteWarehouse(w);
        setDeleteUsage(null);
        setDeleteConfirm("");
        setDeleteUsageLoading(true);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(w.warehouse)}/usage`, {});
            if (!res.ok) {
                toast.error(t("server_error"));
                setDeleteWarehouse(null);
                return;
            }
            setDeleteUsage(await res.json());
        } catch {
            toast.error(t("server_error"));
            setDeleteWarehouse(null);
        } finally {
            setDeleteUsageLoading(false);
        }
    };
    const submitDeleteWarehouse = async () => {
        if (!deleteWarehouse || deleteSubmitting || readOnly) return;
        if (deleteConfirm !== deleteWarehouse.warehouse) return;
        setDeleteSubmitting(true);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(deleteWarehouse.warehouse)}`, {
                method: "DELETE",
            });
            if (res.ok) {
                setRows(curr => curr.filter(r => r.warehouse !== deleteWarehouse.warehouse));
                setHiddenRows(curr => curr.filter(r => r.warehouse !== deleteWarehouse.warehouse));
                toast.success(t("warehouse_deleted", { name: deleteWarehouse.warehouse }));
                setDeleteWarehouse(null);
                setDeleteUsage(null);
                setDeleteConfirm("");
            } else if (res.status === 409) {
                // Raced — something started using it since we checked.
                // Show the fresh breakdown instead of closing.
                const body: { usage?: WarehouseUsage } = await res.json().catch(() => ({}));
                if (body.usage) setDeleteUsage(body.usage);
                setDeleteConfirm("");
                toast.error(t("warehouse_delete_failed"));
            } else {
                toast.error(t("warehouse_delete_failed"));
            }
        } catch {
            toast.error(t("server_error"));
        } finally {
            setDeleteSubmitting(false);
        }
    };

    // Admin-position gate for the Danger Zone. Pulled from localStorage to
    // match the rest of the app; BE endpoints are also gated server-side.
    const isSuperAdmin = useMemo(() => {
        try {
            const raw = window.localStorage.getItem("user");
            if (!raw) return false;
            const u = JSON.parse(raw);
            return isAdminPosition(u?.position ?? u?.permissions);
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                // Two calls on purpose. `rows` stays visible-only, because it
                // also drives every per-warehouse settings table further down
                // this page — a hidden warehouse must not reappear in those.
                // `hiddenRows` is used by the Warehouses section alone, so a
                // hidden warehouse can still be seen and un-hidden.
                const [res, hiddenRes] = await Promise.all([
                    apiFetch(`/settings`, {}),
                    apiFetch(`/settings?visibility=hidden`, {}),
                ]);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data: WarehouseSettings[] = await res.json();
                if (alive) setRows(data);
                if (hiddenRes.ok) {
                    const hidden: WarehouseSettings[] = await hiddenRes.json();
                    if (alive) setHiddenRows(hidden);
                }
            } catch {
                if (alive) toast.error(t("server_error"), { toastId: "settings-load-err" });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [t]);

    // Deep-link support: when the URL carries a hash (e.g. /admin/settings#box-recommendation
    // arrived at via the search dropdown), open the matching <details>
    // AND every <details> ancestor so a nested sub-section becomes visible
    // without the user having to expand each container by hand. Wait for
    // the loading spinner to clear so the sections are actually mounted;
    // rerun on hash change so re-clicking the same search hit while
    // already on the page still scrolls.
    useEffect(() => {
        if (loading) return;
        const id = location.hash.replace(/^#/, "");
        if (!id) return;
        const el = document.getElementById(id);
        if (!(el instanceof HTMLElement)) return;

        // Open the target itself if it's a <details>, then walk up the
        // tree opening any other <details> on the way to the root.
        if (el instanceof HTMLDetailsElement) el.open = true;
        let parent: HTMLElement | null = el.parentElement;
        while (parent) {
            if (parent instanceof HTMLDetailsElement) parent.open = true;
            parent = parent.parentElement;
        }

        // Defer one frame so the freshly-opened bodies have their layout
        // pass before scrollIntoView measures the target row.
        requestAnimationFrame(() => {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }, [loading, location.hash]);

    const toggle = async (warehouse: string, field: WarehouseToggleField, next: boolean) => {
        const prev = rows;
        const key = `${warehouse}:${field}`;
        const row = rows.find(r => r.warehouse === warehouse);

        // PPd is the source of truth — when on, the warehouse cannot
        // reopen totes (PPd already has the items recorded) and cannot
        // share book-ins between users (each PPd transition is owned).
        // Block any attempt to enable the conflicting toggles instead
        // of silently letting them save.
        if (next && (field === "allow_multi_user_book_in" || field === "allow_tote_reopen")) {
            if (row?.send_to_ppd) {
                toast.info(t("toggle_blocked_by_ppd"), { toastId: `blocked-${field}` });
                return;
            }
        }

        // Off-Tote requires Item-Level. Enabling Off-Tote also enables
        // Item-Level (if it isn't already) so the saved state always matches
        // the hint copy ("Requires Item-Level").
        const alsoEnableItemLevel =
            field === "allow_out_of_tote_put_away"
            && next
            && row != null
            && !row.allow_item_level_put_away;

        const patch: Partial<Record<WarehouseToggleField, boolean>> = { [field]: next };
        if (alsoEnableItemLevel) patch.allow_item_level_put_away = true;

        // When PPd is enabled, force the conflicting toggles off in the
        // same PATCH so the persisted state is internally consistent.
        if (field === "send_to_ppd" && next) {
            if (row?.allow_multi_user_book_in) patch.allow_multi_user_book_in = false;
            if (row?.allow_tote_reopen)        patch.allow_tote_reopen = false;
        }

        setRows(rows.map(r =>
            r.warehouse === warehouse ? { ...r, ...patch } : r
        ));
        setPendingKey(key);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            setRows(curr => curr.map(r => r.warehouse === warehouse ? updated : r));
        } catch {
            setRows(prev);
            toast.error(t("server_error"), { toastId: "settings-save-err" });
        } finally {
            setPendingKey(null);
        }
    };

    const updateScalarTimer = (field: ScalarTimerField) => (warehouse: string, value: number | null) => {
        setRows(curr => curr.map(r => r.warehouse === warehouse ? { ...r, [field]: value } : r));
    };

    const updateScalarNum = (field: ScalarNumField) => (warehouse: string, value: number | null) => {
        setRows(curr => curr.map(r => r.warehouse === warehouse ? { ...r, [field]: value } : r));
    };

    // Save a single string/enum setting (e.g. a dropdown) with optimistic update.
    const saveStringField = async (warehouse: string, field: keyof WarehouseSettings, value: string) => {
        const prev = rows;
        const key = `${warehouse}:${field}`;
        setRows(rows.map(r => r.warehouse === warehouse ? { ...r, [field]: value } : r));
        setPendingKey(key);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ [field]: value }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            setRows(curr => curr.map(r => r.warehouse === warehouse ? updated : r));
        } catch {
            setRows(prev);
            toast.error(t("server_error"), { toastId: "settings-save-err" });
        } finally {
            setPendingKey(null);
        }
    };

    const toggleModuleRole = async (warehouse: string, moduleKey: string, role: Role, next: boolean) => {
        const prev = rows;
        const key = `${warehouse}:home_module:${moduleKey}:${role}`;
        const current = rows.find(r => r.warehouse === warehouse)?.home_modules_visible ?? {};
        const currentEntry: RoleVisibility = current[moduleKey] ?? {};
        const nextEntry: RoleVisibility = { ...currentEntry, [role]: next };
        const nextMap: HomeModuleVisibilityMap = { ...current, [moduleKey]: nextEntry };
        setRows(rows.map(r => r.warehouse === warehouse ? { ...r, home_modules_visible: nextMap } : r));
        setPendingKey(key);
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ home_modules_visible: nextMap }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const updated: WarehouseSettings = await res.json();
            setRows(curr => curr.map(r => r.warehouse === warehouse ? updated : r));
        } catch {
            setRows(prev);
            toast.error(t("server_error"), { toastId: "settings-save-err" });
        } finally {
            setPendingKey(null);
        }
    };

    return (
        <div className="page-data page-data-wide admin-settings-page">
            <AdminPageHeader
                titleKey="settings"
                backTo="/admin"
                crumbs={[{ labelKey: "admin_panel", to: "/admin" }, { labelKey: "settings" }]}
            />

            {loading ? (
                <div className="admin-settings-loading"><FlowLoader /></div>
            ) : (
                <>
                <details id="developer" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Wrench size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("developer_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <div className="admin-settings-table">
                            <div className="admin-settings-row">
                                <div>
                                    <strong>{t("demo_mode")}</strong>
                                    <div className="admin-settings-section-hint" style={{ margin: "4px 0 0", fontSize: "0.8rem" }}>
                                        {t("demo_mode_hint")}
                                    </div>
                                </div>
                                <label className="admin-settings-toggle">
                                    <input
                                        type="checkbox"
                                        checked={demoOn}
                                        onChange={e => setDemoMode(e.target.checked)}
                                    />
                                    <span className="admin-settings-slider" />
                                </label>
                            </div>
                            <div className="admin-settings-row">
                                <div>
                                    <strong>{t("demo_mode_super_admin_only")}</strong>
                                    <div className="admin-settings-section-hint" style={{ margin: "4px 0 0", fontSize: "0.8rem" }}>
                                        {t("demo_mode_super_admin_only_hint")}
                                    </div>
                                </div>
                                <label className="admin-settings-toggle">
                                    <input
                                        type="checkbox"
                                        checked={demoAudience === "super-admin"}
                                        onChange={e => setDemoAudience(e.target.checked ? "super-admin" : "all-admins")}
                                    />
                                    <span className="admin-settings-slider" />
                                </label>
                            </div>
                        </div>
                    </div>
                </details>

                <details id="warehouses" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Building2 size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("warehouses_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("warehouses_settings_hint")}</p>
                        <div className="admin-settings-table">
                            <div className="admin-settings-row admin-settings-row--head">
                                <div>{t("warehouse_col")}</div>
                                <div>{t("country_col")}</div>
                            </div>
                            {rows.length === 0 && hiddenRows.length === 0 ? (
                                <div className="admin-settings-row">
                                    <div style={{ color: "var(--color-text-muted)" }}>{t("loading")}</div>
                                </div>
                            ) : (
                                // Hidden warehouses are listed here — and ONLY here —
                                // greyed out, so they can be found and brought back.
                                [...rows, ...hiddenRows]
                                    .sort((a, b) => a.warehouse.localeCompare(b.warehouse))
                                    .map(r => {
                                    const isHidden = !!r.hidden_at;
                                    return (
                                    <div
                                        key={r.warehouse}
                                        className="admin-settings-row"
                                        style={isHidden ? { opacity: 0.55 } : undefined}
                                    >
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                                            <strong>{r.warehouse}</strong>
                                            {isHidden && (
                                                <span style={{
                                                    fontSize: "0.7rem",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    padding: "1px 6px",
                                                    borderRadius: "var(--radius-sm, 6px)",
                                                    border: "1px solid var(--color-border, #ccc)",
                                                    color: "var(--color-text-muted)",
                                                }}>
                                                    {t("warehouse_hidden_pill")}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="locations-table-btn"
                                                onClick={() => openEditWarehouse(r)}
                                                disabled={readOnly}
                                                aria-disabled={readOnly}
                                                aria-label={t("edit")}
                                                title={readOnly ? t("read_only_mode") : t("edit")}
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            {isSuperAdmin && (
                                                <button
                                                    type="button"
                                                    className="locations-table-btn"
                                                    onClick={() => toggleWarehouseVisibility(r)}
                                                    disabled={visibilityBusy === r.warehouse || readOnly}
                                                    aria-disabled={readOnly}
                                                    aria-label={isHidden ? t("warehouse_show") : t("warehouse_hide")}
                                                    title={readOnly ? t("read_only_mode") : (isHidden ? t("warehouse_show_hint") : t("warehouse_hide_hint"))}
                                                >
                                                    {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                                                </button>
                                            )}
                                            {isSuperAdmin && (
                                                <button
                                                    type="button"
                                                    className="locations-table-btn"
                                                    onClick={() => openDeleteWarehouse(r)}
                                                    disabled={readOnly}
                                                    aria-disabled={readOnly}
                                                    aria-label={t("warehouse_delete")}
                                                    title={readOnly ? t("read_only_mode") : t("warehouse_delete")}
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                        <div>{r.country || <span style={{ color: "var(--color-text-muted)" }}>—</span>}</div>
                                    </div>
                                    );
                                })
                            )}
                        </div>
                        {isSuperAdmin && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                style={{ marginTop: 12 }}
                                onClick={() => setAddWarehouseOpen(true)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Plus size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                                {t("warehouse_add")}
                            </button>
                        )}
                    </div>
                </details>

                <details id="goods-in" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Truck size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("goods_in_settings")}</h2>
                    </summary>

                    <div className="admin-settings-section-body">
                        <details id="scan-sheet" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("scan_sheet_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">
                                    {t("targets_moved_to_points")}{" "}
                                    <Link to="/admin/warehouse/points">{t("points_settings_title")}</Link>
                                </p>

                                <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                                    {t("bf_hot_cover_days")}
                                </h3>
                                <p className="admin-settings-section-hint">{t("bf_hot_cover_days_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="bf_hot_cover_days"
                                    placeholder={14}
                                    unit={t("picking_unit_days")}
                                    step={1}
                                    max={3650}
                                    onSaved={updateScalarNum("bf_hot_cover_days")}
                                />

                                <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                                    {t("bf_big_max_length_cm")}
                                </h3>
                                <p className="admin-settings-section-hint">{t("bf_big_max_length_cm_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="bf_big_max_length_cm"
                                    placeholder={60}
                                    unit={t("pallets_height_unit")}
                                    step={1}
                                    max={500}
                                    onSaved={updateScalarNum("bf_big_max_length_cm")}
                                />

                                <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                                    {t("bf_new_sku_hot_units")}
                                </h3>
                                <p className="admin-settings-section-hint">{t("bf_new_sku_hot_units_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="bf_new_sku_hot_units"
                                    placeholder={2}
                                    unit={t("bf_unit_units")}
                                    step={1}
                                    max={100}
                                    onSaved={updateScalarNum("bf_new_sku_hot_units")}
                                />

                                <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                                    {t("bf_tote_max_weight_kg")}
                                </h3>
                                <p className="admin-settings-section-hint">{t("bf_tote_max_weight_kg_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="bf_tote_max_weight_kg"
                                    placeholder={10}
                                    unit="kg"
                                    step={0.5}
                                    max={1000}
                                    onSaved={updateScalarNum("bf_tote_max_weight_kg")}
                                />

                                <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                                    {t("scansheet_locations_settings")}
                                </h3>
                                <p className="admin-settings-section-hint">
                                    {t("scansheet_locations_settings_hint")}
                                </p>
                                <ScansheetLocationsAdmin />
                            </div>
                        </details>

                        <details id="book-in" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("book_in_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <h3 className="admin-settings-subsection-title">
                                    {t("warehouse_toggles")}
                                </h3>
                                <p className="admin-settings-section-hint">
                                    {t("warehouse_toggles_hint")}
                                </p>
                                <div className="admin-settings-table admin-settings-table--8col">
                                    <div className="admin-settings-row admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div>
                                            {t("allow_duplicate_barcodes")}
                                            <HeadInfo text={t("allow_duplicate_barcodes_hint")} />
                                        </div>
                                        <div title={t("send_to_ppd_hint")}>
                                            {t("send_to_ppd")}
                                        </div>
                                        <div title={t("allow_multi_user_book_in_hint")}>
                                            {t("allow_multi_user_book_in")}
                                        </div>
                                        <div title={t("allow_tote_reopen_hint")}>
                                            {t("allow_tote_reopen")}
                                        </div>
                                        <div title={t("bulk_qty_book_in_hint")}>
                                            {t("bulk_qty_book_in")}
                                        </div>
                                        <div title={t("manifest_required_book_in_hint")}>
                                            {t("manifest_required_book_in")}
                                        </div>
                                        <div>
                                            {t("book_in_duplicate_stock_check")}
                                            <HeadInfo text={t("book_in_duplicate_stock_check_hint")} />
                                        </div>
                                    </div>
                                    {rows.map(r => {
                                        // When PPd is on the two conflicting
                                        // toggles are forced off and locked.
                                        // The toggle handler refuses to flip
                                        // them on, but disabling here makes
                                        // the constraint visible.
                                        const ppdLocks = r.send_to_ppd;
                                        return (
                                            <div key={r.warehouse} className="admin-settings-row">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.allow_duplicate_barcodes}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:allow_duplicate_barcodes`}
                                                            onChange={e => toggle(r.warehouse, "allow_duplicate_barcodes", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.send_to_ppd}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:send_to_ppd`}
                                                            onChange={e => toggle(r.warehouse, "send_to_ppd", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div title={ppdLocks ? t("toggle_blocked_by_ppd") : undefined}>
                                                    <label className={`admin-settings-toggle${ppdLocks ? " admin-settings-toggle--locked" : ""}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={r.allow_multi_user_book_in && !ppdLocks}
                                                            disabled={ppdLocks || pendingKey === `${r.warehouse}:allow_multi_user_book_in`}
                                                            onChange={e => toggle(r.warehouse, "allow_multi_user_book_in", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div title={ppdLocks ? t("toggle_blocked_by_ppd") : undefined}>
                                                    <label className={`admin-settings-toggle${ppdLocks ? " admin-settings-toggle--locked" : ""}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={r.allow_tote_reopen && !ppdLocks}
                                                            disabled={ppdLocks || pendingKey === `${r.warehouse}:allow_tote_reopen`}
                                                            onChange={e => toggle(r.warehouse, "allow_tote_reopen", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.bulk_qty_book_in}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:bulk_qty_book_in`}
                                                            onChange={e => toggle(r.warehouse, "bulk_qty_book_in", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!r.manifest_required_book_in}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:manifest_required_book_in`}
                                                            onChange={e => toggle(r.warehouse, "manifest_required_book_in", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.book_in_duplicate_stock_check === true}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:book_in_duplicate_stock_check`}
                                                            onChange={e => toggle(r.warehouse, "book_in_duplicate_stock_check", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>

                                <p className="admin-settings-section-hint">
                                    {t("targets_moved_to_points")}{" "}
                                    <Link to="/admin/warehouse/points">{t("points_settings_title")}</Link>
                                </p>
                            </div>
                        </details>

                        <details id="inbound" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("inbound_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <h3 className="admin-settings-subsection-title">{t("inbound_stock_types")}</h3>
                                <p className="admin-settings-section-hint">{t("inbound_stock_types_hint")}</p>
                                <InboundStockTypesEditor
                                    rows={rows}
                                    onSaved={(warehouse, next) => setRows(curr => curr.map(r =>
                                        r.warehouse === warehouse
                                            ? { ...r, inbound_stock_types: next }
                                            : r,
                                    ))}
                                />

                                <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                                    {t("inbound_pallet_age_thresholds")}
                                </h3>
                                <p className="admin-settings-section-hint">{t("inbound_pallet_age_thresholds_hint")}</p>
                                <h4 className="admin-settings-group-title">{t("inbound_pallet_age_warn_days")}</h4>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="inbound_pallet_age_warn_days"
                                    placeholder={0}
                                    unit={t("picking_unit_days")}
                                    step={1}
                                    max={365}
                                    onSaved={updateScalarNum("inbound_pallet_age_warn_days")}
                                />
                                <h4 className="admin-settings-group-title">{t("inbound_pallet_age_alert_days")}</h4>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="inbound_pallet_age_alert_days"
                                    placeholder={0}
                                    unit={t("picking_unit_days")}
                                    step={1}
                                    max={365}
                                    onSaved={updateScalarNum("inbound_pallet_age_alert_days")}
                                />
                            </div>
                        </details>

                        <details id="pallets" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("pallets_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("pallets_settings_hint")}</p>
                                <p className="admin-settings-section-hint">{t("pallets_settings_types_hint")} {t("pallets_settings_sizes_hint")}</p>
                                <p className="admin-settings-section-hint">{t("pallets_settings_next_seq_hint")}</p>
                                <PalletSettingsEditor
                                    rows={rows}
                                    onSaved={(warehouse, next) => setRows(curr => curr.map(r =>
                                        r.warehouse === warehouse
                                            ? { ...r, pallet_settings: next }
                                            : r,
                                    ))}
                                />
                            </div>
                        </details>
                    </div>
                </details>

                <details id="put-away" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><PackageCheck size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("put_away_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <h3 className="admin-settings-subsection-title">
                            {t("put_away_warehouse_toggles")}
                        </h3>
                        <p className="admin-settings-section-hint">
                            {t("put_away_warehouse_toggles_hint")}
                        </p>
                        <div className="admin-settings-table admin-settings-table--3col">
                            <div className="admin-settings-row admin-settings-row--head">
                                <div>{t("warehouse_col")}</div>
                                <div title={t("allow_item_level_put_away_hint")}>
                                    {t("allow_item_level_put_away")}
                                </div>
                                <div title={t("allow_out_of_tote_put_away_hint")}>
                                    {t("allow_out_of_tote_put_away")}
                                </div>
                            </div>
                            {rows.map(r => (
                                <div key={r.warehouse} className="admin-settings-row">
                                    <div className="admin-settings-warehouse">{r.warehouse}</div>
                                    <div>
                                        <label className="admin-settings-toggle">
                                            <input
                                                type="checkbox"
                                                checked={r.allow_item_level_put_away}
                                                disabled={readOnly || pendingKey === `${r.warehouse}:allow_item_level_put_away`}
                                                onChange={e => toggle(r.warehouse, "allow_item_level_put_away", e.target.checked)}
                                            />
                                            <span className="admin-settings-slider" />
                                        </label>
                                    </div>
                                    <div>
                                        <label className="admin-settings-toggle">
                                            <input
                                                type="checkbox"
                                                checked={r.allow_out_of_tote_put_away}
                                                disabled={readOnly || pendingKey === `${r.warehouse}:allow_out_of_tote_put_away`}
                                                onChange={e => toggle(r.warehouse, "allow_out_of_tote_put_away", e.target.checked)}
                                            />
                                            <span className="admin-settings-slider" />
                                        </label>
                                    </div>
                                </div>
                            ))}
                            {rows.length === 0 && (
                                <div className="admin-settings-empty">{t("no_warehouses")}</div>
                            )}
                        </div>

                        <p className="admin-settings-section-hint">
                        {t("targets_moved_to_points")}{" "}
                        <Link to="/admin/warehouse/points">{t("points_settings_title")}</Link>
                                </p>
                    </div>
                </details>

                <details id="leaders-bonus" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Trophy size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("leaders_bonus_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("leaders_bonus_settings_hint")}</p>
                        <LeadersBonusSettings />
                    </div>
                </details>

                <details id="picking" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><ListChecks size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("picking_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("picking_settings_hint")}</p>

                        {/* Every child is a collapsed sub-section, ordered by
                            the workflow: name the queues, decide how a wave is
                            built, tune each pick type, then the picker's screen,
                            then replenishment, then experiments. */}

                        <details id="picking-queues" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_queues_section")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("settings_picking_queues_hint")}</p>
                                <PickingQueueNamesEditor
                                    rows={rows}
                                    pendingKey={pendingKey}
                                    onSave={(warehouse, field, value) => {
                                        void saveStringField(warehouse, field, value);
                                    }}
                                />
                            </div>
                        </details>

                        <details id="picking-wave-building" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_wave_building_section")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_wave_building_section_hint")}</p>

                                <SettingBlock title={t("picking_alloc_method")}>
                                    <p className="admin-settings-section-hint">{t("picking_alloc_method_hint")}</p>
                                    <PickingAllocationMethod />
                                </SettingBlock>

                                {/* The wave-building flags in one table rather
                                    than a stack of single-column ones — the
                                    warehouses line up for comparison. */}
                                <SettingBlock title={t("picking_wave_building_toggles")}>
                                    <div className="admin-settings-table admin-settings-table--4col">
                                        <div className="admin-settings-row admin-settings-row--head">
                                            <div>{t("warehouse_col")}</div>
                                            <div title={t("picking_location_grouping_enabled_hint")}>{t("picking_location_grouping_enabled")}</div>
                                            <div title={t("picking_local_first_enabled_hint")}>{t("picking_local_first_enabled")}</div>
                                            <div title={t("auto_release_short_hint")}>{t("auto_release_short")}</div>
                                        </div>
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.picking_location_grouping_enabled}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_location_grouping_enabled`}
                                                            onChange={e => toggle(r.warehouse, "picking_location_grouping_enabled", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.picking_local_first_enabled}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_local_first_enabled`}
                                                            onChange={e => toggle(r.warehouse, "picking_local_first_enabled", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.auto_release_short === true}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:auto_release_short`}
                                                            onChange={e => toggle(r.warehouse, "auto_release_short", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                                <SettingBlock title={t("picking_anchor_count")}>
                                    <p className="admin-settings-section-hint">{t("picking_anchor_count_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_anchor_count"
                                        placeholder={2}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_anchor_count")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_auto_pause")}>
                                    <p className="admin-settings-section-hint">{t("picking_auto_pause_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_auto_pause_minutes"
                                        placeholder={60}
                                        unit={t("picking_unit_minutes")}
                                        step={1}
                                        max={1440}
                                        onSaved={updateScalarNum("picking_auto_pause_minutes")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_lookahead")}>
                                    <p className="admin-settings-section-hint">{t("picking_lookahead_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_lookahead_orders"
                                        placeholder={2}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={5}
                                        onSaved={updateScalarNum("picking_lookahead_orders")}
                                    />
                                </SettingBlock>
                            </div>
                        </details>

                        <details id="picking-pick-and-sort" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_pick_and_sort_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <SettingBlock title={t("picking_max_wave_orders")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_max_wave_orders"
                                        placeholder={24}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_max_wave_orders")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_pas_large_wave_enabled")}>
                                    <p className="admin-settings-section-hint">{t("picking_pas_large_wave_enabled_hint")}</p>
                                    <div className="admin-settings-table">
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!r.picking_pas_large_wave_enabled}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_pas_large_wave_enabled`}
                                                            onChange={e => toggle(r.warehouse, "picking_pas_large_wave_enabled", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                                <SettingBlock title={t("picking_pas_large_wave_orders")}>
                                    <p className="admin-settings-section-hint">{t("picking_pas_large_wave_orders_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_pas_large_wave_orders"
                                        placeholder={48}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_pas_large_wave_orders")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_pas_large_max_items")}>
                                    <p className="admin-settings-section-hint">{t("picking_pas_large_max_items_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_pas_large_max_items"
                                        placeholder={0}
                                        unit={t("picking_unit_items")}
                                        step={1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_pas_large_max_items")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_pas_large_max_order_kg")}>
                                    <p className="admin-settings-section-hint">{t("picking_pas_large_max_order_kg_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_pas_large_max_order_kg"
                                        placeholder={0}
                                        unit="kg"
                                        step={0.1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_pas_large_max_order_kg")}
                                    />
                                </SettingBlock>
                            </div>
                        </details>

                        {/* Put wall — a Pick & Sort wave variant sorted on a
                            72-slot wall instead of a bay. Only orders that fit
                            whole into one tote qualify; the tote dims x fill %
                            define "fit". The bays themselves live under
                            Sorting > Put Wall. */}
                        <details id="picking-put-wall" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_put_wall")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_put_wall_hint")}</p>
                                <SettingBlock title={t("picking_put_wall_enabled")}>
                                    <p className="admin-settings-section-hint">{t("picking_put_wall_enabled_hint")}</p>
                                    <div className="admin-settings-table">
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!r.picking_put_wall_enabled}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_put_wall_enabled`}
                                                            onChange={e => toggle(r.warehouse, "picking_put_wall_enabled", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                                <SettingBlock title={t("picking_put_wall_wave_orders")}>
                                    <p className="admin-settings-section-hint">{t("picking_put_wall_wave_orders_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_wave_orders"
                                        placeholder={72}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_put_wall_wave_orders")}
                                    />
                                </SettingBlock>
                                <h4 id="picking-put-wall-tote" className="admin-settings-group-title">{t("picking_put_wall_tote")}</h4>
                                <p className="admin-settings-section-hint">{t("picking_put_wall_tote_hint")}</p>
                                <SettingBlock title={t("picking_put_wall_tote_l_cm")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_tote_l_cm"
                                        placeholder={51.5}
                                        unit="cm"
                                        step={0.1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_put_wall_tote_l_cm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_put_wall_tote_w_cm")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_tote_w_cm"
                                        placeholder={37.5}
                                        unit="cm"
                                        step={0.1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_put_wall_tote_w_cm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_put_wall_tote_h_cm")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_tote_h_cm"
                                        placeholder={26.5}
                                        unit="cm"
                                        step={0.1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_put_wall_tote_h_cm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_put_wall_fill_pct")}>
                                    <p className="admin-settings-section-hint">{t("picking_put_wall_fill_pct_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_fill_pct"
                                        placeholder={75}
                                        unit="%"
                                        step={1}
                                        max={100}
                                        onSaved={updateScalarNum("picking_put_wall_fill_pct")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_put_wall_min_orders")}>
                                    <p className="admin-settings-section-hint">{t("picking_put_wall_min_orders_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_min_orders"
                                        placeholder={24}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_put_wall_min_orders")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_put_wall_max_totes")}>
                                    <p className="admin-settings-section-hint">{t("picking_put_wall_max_totes_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_put_wall_max_totes"
                                        placeholder={1}
                                        unit={t("picking_unit_totes")}
                                        step={1}
                                        max={5}
                                        onSaved={updateScalarNum("picking_put_wall_max_totes")}
                                    />
                                </SettingBlock>
                            </div>
                        </details>

                        <details id="picking-pairing" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_pairing_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_pairing_hint")}</p>
                                <SettingBlock title={t("picking_ps_pairing_enabled")}>
                                    <p className="admin-settings-section-hint">{t("picking_ps_pairing_enabled_hint")}</p>
                                    <div className="admin-settings-table">
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!r.picking_ps_pairing_enabled}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_ps_pairing_enabled`}
                                                            onChange={e => toggle(r.warehouse, "picking_ps_pairing_enabled", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                                <SettingBlock title={t("picking_ps_segment_split_pct")}>
                                    <p className="admin-settings-section-hint">{t("picking_ps_segment_split_pct_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_ps_segment_split_pct"
                                        placeholder={50}
                                        unit="%"
                                        step={5}
                                        max={80}
                                        onSaved={updateScalarNum("picking_ps_segment_split_pct")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_ps_pair_min_units")}>
                                    <p className="admin-settings-section-hint">{t("picking_ps_pair_min_units_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_ps_pair_min_units"
                                        placeholder={100}
                                        unit={t("picking_card_items")}
                                        step={10}
                                        max={5000}
                                        onSaved={updateScalarNum("picking_ps_pair_min_units")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_ps_sort_on_first_segment")}>
                                    <p className="admin-settings-section-hint">{t("picking_ps_sort_on_first_segment_hint")}</p>
                                    <div className="admin-settings-table">
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.picking_ps_sort_on_first_segment !== false}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_ps_sort_on_first_segment`}
                                                            onChange={e => toggle(r.warehouse, "picking_ps_sort_on_first_segment", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                            </div>
                        </details>

                        <details id="picking-light" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_light_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <SettingBlock title={t("picking_light_max_wave_orders")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_light_max_wave_orders"
                                        placeholder={60}
                                        unit={t("picking_unit_orders")}
                                        step={1}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_light_max_wave_orders")}
                                    />
                                </SettingBlock>
                            </div>
                        </details>

                        {/* Named for the pick type, not for the kind of
                            setting inside it — same as its Pick & Sort and
                            Single siblings. The two groups below answer
                            different questions (what makes an order Large
                            vs how many cages picking it needs), so the
                            thresholds hint is scoped to its own group
                            rather than sitting at the top governing both. */}
                        <details id="picking-large" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_large_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_large_settings_hint")}</p>
                                <h4 id="picking-large-thresholds" className="admin-settings-group-title">{t("picking_large_thresholds")}</h4>
                                <p className="admin-settings-section-hint">{t("picking_large_thresholds_hint")}</p>
                                <SettingBlock title={t("picking_large_max_items")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_items"
                                        placeholder={0}
                                        unit={t("picking_unit_items")}
                                        step={1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_large_max_items")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_large_max_order_kg")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_order_kg"
                                        placeholder={0}
                                        unit="kg"
                                        step={0.1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_large_max_order_kg")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_large_max_order_cbm")}>
                                    <p className="admin-settings-section-hint">{t("picking_large_max_order_cbm_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_order_cbm"
                                        placeholder={0}
                                        unit="CBM"
                                        step={0.01}
                                        max={1000}
                                        onSaved={updateScalarNum("picking_large_max_order_cbm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_large_max_item_grams")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_item_grams"
                                        placeholder={0}
                                        unit="g"
                                        step={1}
                                        max={10_000_000}
                                        onSaved={updateScalarNum("picking_large_max_item_grams")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_large_max_item_l_cm")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_item_l_cm"
                                        placeholder={0}
                                        unit="cm"
                                        step={0.1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_large_max_item_l_cm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_large_max_item_w_cm")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_item_w_cm"
                                        placeholder={0}
                                        unit="cm"
                                        step={0.1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_large_max_item_w_cm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_large_max_item_h_cm")}>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_large_max_item_h_cm"
                                        placeholder={0}
                                        unit="cm"
                                        step={0.1}
                                        max={100000}
                                        onSaved={updateScalarNum("picking_large_max_item_h_cm")}
                                    />
                                </SettingBlock>
                                {/* Capacity first, then the percentage of it
                                    that is realistically usable: the two
                                    multiply. */}
                                <h4 id="picking-large-roll-cages" className="admin-settings-group-title">{t("picking_large_roll_cages")}</h4>
                                <p className="admin-settings-section-hint">{t("picking_large_roll_cages_hint")}</p>
                                <SettingBlock title={t("picking_roll_cage_cbm")}>
                                    <p className="admin-settings-section-hint">{t("picking_roll_cage_cbm_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_roll_cage_cbm"
                                        placeholder={0}
                                        unit="CBM"
                                        step={0.01}
                                        max={100}
                                        onSaved={updateScalarNum("picking_roll_cage_cbm")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_roll_cage_utilisation_pct")}>
                                    <p className="admin-settings-section-hint">{t("picking_roll_cage_utilisation_pct_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_roll_cage_utilisation_pct"
                                        placeholder={0}
                                        unit="%"
                                        step={1}
                                        max={100}
                                        onSaved={updateScalarNum("picking_roll_cage_utilisation_pct")}
                                    />
                                </SettingBlock>
                            </div>
                        </details>

                        <details id="picking-picker-screen" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_picker_screen_section")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_picker_screen_section_hint")}</p>

                                <SettingBlock title={t("picking_picker_screen_toggles")}>
                                    <div className="admin-settings-table admin-settings-table--3col">
                                        <div className="admin-settings-row admin-settings-row--head">
                                            <div>{t("warehouse_col")}</div>
                                            <div title={t("picking_require_location_scan_hint")}>{t("picking_require_location_scan")}</div>
                                            <div title={t("picking_show_product_image_hint")}>{t("picking_show_product_image")}</div>
                                        </div>
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.picking_require_location_scan}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_require_location_scan`}
                                                            onChange={e => toggle(r.warehouse, "picking_require_location_scan", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.picking_show_product_image}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:picking_show_product_image`}
                                                            onChange={e => toggle(r.warehouse, "picking_show_product_image", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                                <SettingBlock title={t("picking_other_skus_count")}>
                                    <p className="admin-settings-section-hint">{t("picking_other_skus_count_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="picking_other_skus_count"
                                        placeholder={1}
                                        unit={t("picking_unit_skus")}
                                        step={1}
                                        max={10}
                                        onSaved={updateScalarNum("picking_other_skus_count")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_location_change_ms")}>
                                    <p className="admin-settings-section-hint">{t("picking_location_change_ms_hint")}</p>
                                    <ScalarTimerEditor
                                        rows={rows}
                                        field="picking_location_change_ms"
                                        placeholderMs={2000}
                                        onSaved={updateScalarTimer("picking_location_change_ms")}
                                    />
                                </SettingBlock>
                                <SettingBlock title={t("picking_new_location_highlight_ms")}>
                                    <p className="admin-settings-section-hint">{t("picking_new_location_highlight_ms_hint")}</p>
                                    <ScalarTimerEditor
                                        rows={rows}
                                        field="picking_new_location_highlight_ms"
                                        placeholderMs={6000}
                                        onSaved={updateScalarTimer("picking_new_location_highlight_ms")}
                                    />
                                </SettingBlock>
                            </div>
                        </details>

                        <details id="picking-shelf-drop" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_shelf_drop_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_shelf_drop_settings_hint")}</p>

                                <SettingBlock title={t("drop_claim_ttl_minutes")}>
                                    <p className="admin-settings-section-hint">{t("drop_claim_ttl_minutes_hint")}</p>
                                    <ScalarNumEditor
                                        rows={rows}
                                        field="drop_claim_ttl_minutes"
                                        placeholder={10}
                                        unit={t("picking_unit_minutes")}
                                        step={1}
                                        max={120}
                                        onSaved={updateScalarNum("drop_claim_ttl_minutes")}
                                    />
                                </SettingBlock>

                                {/* This setting has existed on the backend since
                                    the Drop modules shipped but was never
                                    rendered — it was only changeable by hand. */}
                                <SettingBlock title={t("require_location_scan_shelf_drop")}>
                                    <p className="admin-settings-section-hint">{t("require_location_scan_shelf_drop_hint")}</p>
                                    <div className="admin-settings-table">
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.require_location_scan_shelf_drop}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:require_location_scan_shelf_drop`}
                                                            onChange={e => toggle(r.warehouse, "require_location_scan_shelf_drop", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                            </div>
                        </details>

                        <details id="picking-forecast-drop" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_forecast_drop_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_forecast_drop_settings_hint")}</p>

                                {/* 2026-09-05: the target is the BargainFox HOT target
                                    (bf_hot_targets: BF products 30-day sales × BF hot cover
                                    days). The old lookback / target-days knobs are retired —
                                    columns kept, never sent. */}
                                <p className="admin-settings-section-hint">{t("forecast_drop_target_source_hint")}</p>

                                <SettingBlock title={t("require_location_scan_forecast_drop")}>
                                    <p className="admin-settings-section-hint">{t("require_location_scan_forecast_drop_hint")}</p>
                                    <div className="admin-settings-table">
                                        {rows.map(r => (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <label className="admin-settings-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={r.require_location_scan_forecast_drop}
                                                            disabled={readOnly || pendingKey === `${r.warehouse}:require_location_scan_forecast_drop`}
                                                            onChange={e => toggle(r.warehouse, "require_location_scan_forecast_drop", e.target.checked)}
                                                        />
                                                        <span className="admin-settings-slider" />
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        {rows.length === 0 && (
                                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                        )}
                                    </div>
                                </SettingBlock>
                            </div>
                        </details>

                        {/* Lazy-mounted: a collapsed <details> still keeps its
                            children mounted, and this one polls for results. */}
                        <details
                            id="picking-experiments"
                            className="admin-settings-subsection"
                            onToggle={e => setPickingExperimentsOpen(e.currentTarget.open)}
                        >
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("picking_experiments_section")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("picking_experiments_section_hint")}</p>
                                <SettingBlock title={t("walk_experiment_title")}>
                                    {pickingExperimentsOpen && (
                                        <WalkPatternExperiment
                                            rows={rows.map(r => ({
                                                warehouse: r.warehouse,
                                                picking_walk_pattern: r.picking_walk_pattern ?? "standard",
                                                picking_walk_experiment_enabled: !!r.picking_walk_experiment_enabled,
                                                picking_walk_experiment_pick_types:
                                                    r.picking_walk_experiment_pick_types ?? ["pick_and_sort", "light"],
                                                picking_walk_experiment_variants:
                                                    r.picking_walk_experiment_variants ?? ["standard", "corridor"],
                                                picking_walk_experiment_started_at: r.picking_walk_experiment_started_at ?? null,
                                                picking_walk_experiment_ends_at: r.picking_walk_experiment_ends_at ?? null,
                                            }))}
                                            onSaved={(warehouse, patch) => {
                                                setRows(curr => curr.map(r =>
                                                    r.warehouse === warehouse ? { ...r, ...patch } : r));
                                            }}
                                        />
                                    )}
                                </SettingBlock>

                                {/* Double-gated on purpose: the layout editor
                                    draws every bay in the warehouse (~1,600 at
                                    Pembroke), so it mounts only once BOTH its
                                    own <details> and the Experiments one above
                                    are open — the same pattern the sorting
                                    lighting panel uses. The id makes
                                    /admin/settings#warehouse-layout work for
                                    free via the hash effect. */}
                                <details
                                    id="warehouse-layout"
                                    className="admin-settings-setting"
                                    onToggle={e => setWarehouseLayoutOpen(e.currentTarget.open)}
                                >
                                    <summary className="admin-settings-setting-summary">
                                        <h4 className="admin-settings-setting-title">{t("wlayout_title")}</h4>
                                    </summary>
                                    <div className="admin-settings-setting-body">
                                        {pickingExperimentsOpen && warehouseLayoutOpen && (
                                            <WarehouseLayoutEditor
                                                warehouses={rows.map(r => r.warehouse)}
                                            />
                                        )}
                                    </div>
                                </details>
                            </div>
                        </details>
                    </div>
                </details>

                <details id="locations" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><MapPin size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("locations_settings_panel")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <h3 className="admin-settings-subsection-title">
                            {t("locations_types_settings")}
                        </h3>
                        <p className="admin-settings-section-hint">
                            {t("locations_types_settings_hint")}
                        </p>
                        <LocationTypesAdmin />

                        <h3 className="admin-settings-subsection-title admin-settings-subsection-title--spaced">
                            {t("locations_sizes_settings")}
                        </h3>
                        <p className="admin-settings-section-hint">
                            {t("locations_sizes_settings_hint")}
                        </p>
                        <LocationSizesAdmin />
                    </div>
                </details>

                <details id="lanes" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Palette size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("lanes_colours_section")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <LaneStatusColoursAdmin />
                    </div>
                </details>

                <details id="sorting" className="admin-settings-section admin-settings-section--collapsible" onToggle={e => setSortingOpen(e.currentTarget.open)}>
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Filter size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("sorting_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        {/* Shared moving-head light config — applies to Sorter 1,
                            Sorter 2 and Pick & Sort across the warehouse. Only mount
                            it while both this section and the subsection are open so
                            its 10s light-agents poll stops when collapsed. */}
                        <details id="sorting-lighting" className="admin-settings-subsection" onToggle={e => setSortingLightingOpen(e.currentTarget.open)}>
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("sorting_shared_lighting")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                {sortingOpen && sortingLightingOpen && (
                                    <SortingSharedLighting warehouses={rows.map(r => r.warehouse)} />
                                )}
                            </div>
                        </details>

                        <details id="sorter-1" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("sorter_1_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">
                                    {t("targets_moved_to_points")}{" "}
                                    <Link to="/admin/warehouse/points">{t("points_settings_title")}</Link>
                                </p>
                                <h3 className="admin-settings-subsection-title" style={{ marginTop: 16 }}>
                                    {t("sorter_bays_grid")}
                                </h3>
                                <SortingBayManager station="sorter_1" warehouses={rows.map(r => r.warehouse)} />
                            </div>
                        </details>

                        <details id="sorter-2" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("sorter_2_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">
                                    {t("targets_moved_to_points")}{" "}
                                    <Link to="/admin/warehouse/points">{t("points_settings_title")}</Link>
                                </p>
                                <h3 className="admin-settings-subsection-title" style={{ marginTop: 16 }}>
                                    {t("sorter_bays_grid")}
                                </h3>
                                <SortingBayManager station="sorter_2" warehouses={rows.map(r => r.warehouse)} />
                            </div>
                        </details>

                        {/* Zone Storage → Portal dispatch cut-over switches. Each
                            closing tag scan either lands the order(s) in Portal's
                            dispatch queue (bind tote, allocate, order → dispatch)
                            or, when off, only writes Mongo for the PPd poller. */}
                        <details id="zone-portal-dispatch" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("zone_portal_dispatch_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("zone_portal_dispatch_hint")}</p>
                                <div className="admin-settings-table admin-settings-table--6col">
                                    <div className="admin-settings-row admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div title={t("zone_dispatch_via_portal_hint")}>
                                            {t("zone_dispatch_via_portal")}
                                        </div>
                                        <div title={t("zone_singles_dispatch_via_portal_hint")}>
                                            {t("zone_singles_dispatch_via_portal")}
                                        </div>
                                        <div title={t("zone_reauction_via_portal_hint")}>
                                            {t("zone_reauction_via_portal")}
                                        </div>
                                        <div title={t("zone_grey_write_off_via_portal_hint")}>
                                            {t("zone_grey_write_off_via_portal")}
                                        </div>
                                        <div title={t("allocate_on_ingest_hint")}>
                                            {t("allocate_on_ingest")}
                                        </div>
                                    </div>
                                    {rows.map(r => (
                                        <div key={r.warehouse} className="admin-settings-row">
                                            <div className="admin-settings-warehouse">{r.warehouse}</div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.zone_dispatch_via_portal === true}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:zone_dispatch_via_portal`}
                                                        onChange={e => toggle(r.warehouse, "zone_dispatch_via_portal", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.zone_singles_dispatch_via_portal === true}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:zone_singles_dispatch_via_portal`}
                                                        onChange={e => toggle(r.warehouse, "zone_singles_dispatch_via_portal", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.zone_reauction_via_portal === true}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:zone_reauction_via_portal`}
                                                        onChange={e => toggle(r.warehouse, "zone_reauction_via_portal", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.zone_grey_write_off_via_portal === true}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:zone_grey_write_off_via_portal`}
                                                        onChange={e => toggle(r.warehouse, "zone_grey_write_off_via_portal", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.allocate_on_ingest === true}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:allocate_on_ingest`}
                                                        onChange={e => toggle(r.warehouse, "allocate_on_ingest", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>
                            </div>
                        </details>

                        <details id="pick-and-sort" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("pick_and_sort")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">
                                    {t("pas_settings_hint")}
                                </p>
                                {/* The put wall is added here as just another
                                    bay (Add Bay → "Put wall" toggle); the BE
                                    keys that row station='put_wall'. Its wave
                                    settings live under Picking > Put Wall. */}
                                <SortingBayManager station="pick_and_sort" warehouses={rows.map(r => r.warehouse)} />
                            </div>
                        </details>
                    </div>
                </details>

                <details id="home-modules" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><LayoutGrid size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("home_modules_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">
                            {t("home_modules_settings_hint")}
                        </p>
                        {rows.map(r => (
                            <details key={r.warehouse} className="admin-settings-subsection">
                                <summary className="admin-settings-subsection-summary">
                                    <h3 className="admin-settings-section-title">{r.warehouse}</h3>
                                </summary>
                                <div className="admin-settings-subsection-body">
                                    <div className="admin-settings-table admin-settings-table--role-modules">
                                        <div className="admin-settings-row admin-settings-row--head">
                                            <div>{t("module_col")}</div>
                                            <div title="SuperAdmin">{t("role_super_admin")}</div>
                                            <div>{t("admin")}</div>
                                            <div>{t("basic")}</div>
                                        </div>
                                        {HOME_MODULES.map(parent => (
                                            <RoleModuleParent
                                                key={parent.key}
                                                warehouse={r.warehouse}
                                                map={r.home_modules_visible}
                                                pendingKey={pendingKey}
                                                parent={parent}
                                                onToggle={toggleModuleRole}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </details>
                        ))}
                        {rows.length === 0 && (
                            <div className="admin-settings-empty">{t("no_warehouses")}</div>
                        )}
                    </div>
                </details>

                <details id="access-control" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><ShieldCheck size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("access_control_settings_title")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">
                            {t("access_control_settings_note")}
                        </p>
                        <Link to="/admin/access-control" className="btn btn-primary btn-sm">
                            {t("access_control_settings_link")}
                        </Link>
                    </div>
                </details>

                <details id="wipe" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Eraser size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("wipe_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("wipe_settings_hint")}</p>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_threshold_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("wipe_threshold_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="wipe_threshold"
                                    placeholder={0}
                                    unit="£"
                                    step={0.01}
                                    max={1000000}
                                    onSaved={updateScalarNum("wipe_threshold")}
                                />
                            </div>
                        </details>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_dispose_threshold_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("wipe_dispose_threshold_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="wipe_dispose_threshold"
                                    placeholder={0}
                                    unit="£"
                                    step={0.01}
                                    max={1000000}
                                    onSaved={updateScalarNum("wipe_dispose_threshold")}
                                />
                            </div>
                        </details>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_box_threshold_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("wipe_box_threshold_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="wipe_box_threshold"
                                    placeholder={0}
                                    unit="£"
                                    step={0.01}
                                    max={1000000}
                                    onSaved={updateScalarNum("wipe_box_threshold")}
                                />
                            </div>
                        </details>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_label_width_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <ScalarNumEditor
                                    rows={rows}
                                    field="wipe_label_width_mm"
                                    placeholder={65}
                                    unit="mm"
                                    step={1}
                                    max={1000}
                                    onSaved={updateScalarNum("wipe_label_width_mm")}
                                />
                            </div>
                        </details>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_label_height_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <ScalarNumEditor
                                    rows={rows}
                                    field="wipe_label_height_mm"
                                    placeholder={38}
                                    unit="mm"
                                    step={1}
                                    max={1000}
                                    onSaved={updateScalarNum("wipe_label_height_mm")}
                                />
                            </div>
                        </details>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_label_orientation_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("wipe_label_orientation_hint")}</p>
                                <div className="admin-settings-table">
                                    {rows.map(r => (
                                        <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                            <div className="admin-settings-warehouse">{r.warehouse}</div>
                                            <div className="admin-settings-scalar-control">
                                                <select
                                                    className="admin-settings-po-input admin-settings-po-input--select"
                                                    value={r.wipe_label_orientation || "ccw"}
                                                    disabled={readOnly || pendingKey === `${r.warehouse}:wipe_label_orientation`}
                                                    onChange={e => saveStringField(r.warehouse, "wipe_label_orientation", e.target.value)}
                                                >
                                                    <option value="normal">{t("wipe_orientation_normal")}</option>
                                                    <option value="cw">{t("wipe_orientation_cw")}</option>
                                                    <option value="ccw">{t("wipe_orientation_ccw")}</option>
                                                    <option value="flip">{t("wipe_orientation_flip")}</option>
                                                </select>
                                                {pendingKey === `${r.warehouse}:wipe_label_orientation` && (
                                                    <span className="admin-settings-po-unit">{t("saving")}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>
                            </div>
                        </details>

                        <details className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">{t("wipe_auto_process_no_label")}</summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("wipe_auto_process_no_hint")}</p>
                                <div className="admin-settings-table">
                                    {rows.map(r => (
                                        <div key={r.warehouse} className="admin-settings-row admin-settings-row--scalar-inline">
                                            <div className="admin-settings-warehouse">{r.warehouse}</div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.wipe_auto_process_no}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:wipe_auto_process_no`}
                                                        onChange={e => toggle(r.warehouse, "wipe_auto_process_no", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>
                            </div>
                        </details>
                    </div>
                </details>

                <details id="dispatch" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Send size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("dispatch_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("dispatch_settings_hint")}</p>

                        <details id="dispatch-toggles" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("dispatch_warehouse_toggles")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("dispatch_warehouse_toggles_hint")}</p>
                                <div className="admin-settings-table admin-settings-table--4col">
                                    <div className="admin-settings-row admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div title={t("dispatch_show_images_hint")}>{t("dispatch_show_images")}</div>
                                        <div title={t("dispatch_box_recommendation_enabled_hint")}>{t("dispatch_box_recommendation_enabled")}</div>
                                        <div />
                                    </div>
                                    {rows.map(r => (
                                        <div key={r.warehouse} className="admin-settings-row">
                                            <div className="admin-settings-warehouse">{r.warehouse}</div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.dispatch_show_images}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:dispatch_show_images`}
                                                        onChange={e => toggle(r.warehouse, "dispatch_show_images", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.dispatch_box_recommendation_enabled}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:dispatch_box_recommendation_enabled`}
                                                        onChange={e => toggle(r.warehouse, "dispatch_box_recommendation_enabled", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div />
                                        </div>
                                    ))}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>
                            </div>
                        </details>

                        <details id="dispatch-shipments-release" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("dispatch_shipments_release")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("dispatch_shipments_release_hint")}</p>
                                <p className="admin-settings-section-hint">{t("dispatch_shipments_immediate_delay_hint")}</p>
                                <div className="admin-settings-table admin-settings-table--release">
                                    <div className="admin-settings-row admin-settings-row--release admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div>{t("dispatch_shipments_release_mode_col")}</div>
                                        <div>{t("dispatch_shipments_release_schedule_col")}</div>
                                    </div>
                                    {rows.map(r => {
                                        const modeKey = `${r.warehouse}:dispatch_shipments_release_mode`;
                                        const timeKey = `${r.warehouse}:dispatch_shipments_release_time`;
                                        const tzKey   = `${r.warehouse}:dispatch_shipments_release_timezone`;
                                        const delayKey = `${r.warehouse}:dispatch_shipments_immediate_delay_mins`;
                                        const releaseTimeHHMM = (r.dispatch_shipments_release_time || "17:00:00").slice(0, 5);
                                        const delayMins = (() => {
                                            const n = Number(r.dispatch_shipments_immediate_delay_mins);
                                            if (!Number.isFinite(n)) return 15;
                                            return Math.min(60, Math.max(0, Math.round(n / 5) * 5));
                                        })();
                                        const tzValue = r.dispatch_shipments_release_timezone || "Europe/London";
                                        const saveRelease = async (patch: Partial<WarehouseSettings>, pendKey: string) => {
                                            const prev = rows;
                                            setRows(curr => curr.map(x => x.warehouse === r.warehouse ? { ...x, ...patch } : x));
                                            setPendingKey(pendKey);
                                            try {
                                                const res = await apiFetch(`/settings/${encodeURIComponent(r.warehouse)}`, {
                                                    method: "PATCH",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify(patch),
                                                });
                                                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                                const updated: WarehouseSettings = await res.json();
                                                setRows(curr => curr.map(x => x.warehouse === r.warehouse ? updated : x));
                                            } catch {
                                                setRows(prev);
                                                toast.error(t("server_error"), { toastId: "settings-save-err" });
                                            } finally {
                                                setPendingKey(null);
                                            }
                                        };
                                        const isCutoff = r.dispatch_shipments_release_mode === "cutoff";
                                        return (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--release">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <select
                                                        className="admin-settings-select"
                                                        value={r.dispatch_shipments_release_mode || "immediate"}
                                                        disabled={pendingKey === modeKey}
                                                        onChange={e => saveRelease({ dispatch_shipments_release_mode: e.target.value as "immediate" | "cutoff" }, modeKey)}
                                                    >
                                                        <option value="immediate">{t("dispatch_shipments_release_immediate")}</option>
                                                        <option value="cutoff">{t("dispatch_shipments_release_cutoff")}</option>
                                                    </select>
                                                </div>
                                                <div className="admin-settings-release-controls">
                                                    {!isCutoff && (
                                                        <label className="admin-settings-release-field">
                                                            <span>{t("dispatch_shipments_immediate_delay")}</span>
                                                            <select
                                                                className="admin-settings-select"
                                                                value={delayMins}
                                                                disabled={pendingKey === delayKey}
                                                                onChange={e => saveRelease({ dispatch_shipments_immediate_delay_mins: Number(e.target.value) }, delayKey)}
                                                            >
                                                                {Array.from({ length: 13 }, (_, i) => i * 5).map(m => (
                                                                    <option key={m} value={m}>{m} min</option>
                                                                ))}
                                                            </select>
                                                        </label>
                                                    )}
                                                    {isCutoff && (
                                                        <>
                                                            <label className="admin-settings-release-field">
                                                                <span>{t("dispatch_shipments_release_time_col")}</span>
                                                                <input
                                                                    type="time"
                                                                    className="admin-settings-input"
                                                                    value={releaseTimeDrafts[r.warehouse] ?? releaseTimeHHMM}
                                                                    disabled={pendingKey === timeKey}
                                                                    onChange={e => {
                                                                        // Draft locally so the picker stays responsive,
                                                                        // then auto-save shortly after the ticks stop —
                                                                        // one PATCH, not one per minute step.
                                                                        const next = e.target.value;
                                                                        setReleaseTimeDrafts(d => ({ ...d, [r.warehouse]: next }));
                                                                        clearTimeout(releaseTimeTimers.current[r.warehouse]);
                                                                        releaseTimeTimers.current[r.warehouse] = setTimeout(() => {
                                                                            if (next && next !== releaseTimeHHMM) {
                                                                                saveRelease({ dispatch_shipments_release_time: `${next}:00` }, timeKey);
                                                                            }
                                                                            setReleaseTimeDrafts(d => {
                                                                                const { [r.warehouse]: _drop, ...rest } = d;
                                                                                return rest;
                                                                            });
                                                                        }, 800);
                                                                    }}
                                                                    onBlur={e => {
                                                                        clearTimeout(releaseTimeTimers.current[r.warehouse]);
                                                                        const next = e.target.value;
                                                                        if (next && next !== releaseTimeHHMM) {
                                                                            saveRelease({ dispatch_shipments_release_time: `${next}:00` }, timeKey);
                                                                        }
                                                                        setReleaseTimeDrafts(d => {
                                                                            const { [r.warehouse]: _drop, ...rest } = d;
                                                                            return rest;
                                                                        });
                                                                    }}
                                                                />
                                                            </label>
                                                            <label className="admin-settings-release-field admin-settings-release-field--wide">
                                                                <span>{t("dispatch_shipments_release_timezone_col")}</span>
                                                                <select
                                                                    className="admin-settings-select"
                                                                    value={tzValue}
                                                                    disabled={pendingKey === tzKey}
                                                                    onChange={e => saveRelease({ dispatch_shipments_release_timezone: e.target.value }, tzKey)}
                                                                >
                                                                    <option value="Europe/London">UK (Europe/London)</option>
                                                                    <option value="Europe/Warsaw">Poland (Europe/Warsaw)</option>
                                                                    <option value="Europe/Dublin">Ireland (Europe/Dublin)</option>
                                                                    <option value="Europe/Paris">France (Europe/Paris)</option>
                                                                    <option value="Europe/Berlin">Germany (Europe/Berlin)</option>
                                                                    <option value="Europe/Amsterdam">Netherlands (Europe/Amsterdam)</option>
                                                                    <option value="Europe/Madrid">Spain (Europe/Madrid)</option>
                                                                    <option value="UTC">UTC</option>
                                                                </select>
                                                            </label>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>
                            </div>
                        </details>

                        <details id="dispatch-pallet-orders" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("pallet_orders_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("pallet_orders_settings_hint")}</p>
                                <div className="admin-settings-table admin-settings-table--release">
                                    <div className="admin-settings-row admin-settings-row--release admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div>{t("pallet_order_status_col")}</div>
                                        <div />
                                    </div>
                                    {rows.map(r => {
                                        const pendKey = `${r.warehouse}:pallet_order_status`;
                                        const value = r.pallet_order_status === "on_hold" ? "on_hold" : "unfulfilled";
                                        const save = async (next: "unfulfilled" | "on_hold") => {
                                            const prev = rows;
                                            setRows(curr => curr.map(x => x.warehouse === r.warehouse ? { ...x, pallet_order_status: next } : x));
                                            setPendingKey(pendKey);
                                            try {
                                                const res = await apiFetch(`/settings/${encodeURIComponent(r.warehouse)}`, {
                                                    method: "PATCH",
                                                    headers: { "Content-Type": "application/json" },
                                                    body: JSON.stringify({ pallet_order_status: next }),
                                                });
                                                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                                const updated: WarehouseSettings = await res.json();
                                                setRows(curr => curr.map(x => x.warehouse === r.warehouse ? updated : x));
                                            } catch {
                                                setRows(prev);
                                                toast.error(t("server_error"), { toastId: "settings-save-err" });
                                            } finally {
                                                setPendingKey(null);
                                            }
                                        };
                                        return (
                                            <div key={r.warehouse} className="admin-settings-row admin-settings-row--release">
                                                <div className="admin-settings-warehouse">{r.warehouse}</div>
                                                <div>
                                                    <select
                                                        className="admin-settings-select"
                                                        aria-label={`${r.warehouse}: ${t("pallet_order_status_col")}`}
                                                        value={value}
                                                        disabled={pendingKey === pendKey}
                                                        onChange={e => save(e.target.value as "unfulfilled" | "on_hold")}
                                                    >
                                                        <option value="unfulfilled">{t("pallet_order_status_unfulfilled")}</option>
                                                        <option value="on_hold">{t("pallet_order_status_on_hold")}</option>
                                                    </select>
                                                </div>
                                                <div className="admin-settings-section-hint">
                                                    {value === "on_hold" ? t("pallet_order_status_on_hold_note") : t("pallet_order_status_unfulfilled_note")}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>
                            </div>
                        </details>

                        <details id="dispatch-channel-rules" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("channel_rules_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("channel_rules_hint")}</p>
                                <ChannelRulesAdmin />
                            </div>
                        </details>

                        <details id="shipping-rules" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("shipping_rules_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("shipping_rules_settings_hint")}</p>
                                <ShippingRulesAdmin warehouses={rows.map(r => r.warehouse)} />
                            </div>
                        </details>

                        <details id="desks" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("desks_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("desks_settings_hint")}</p>
                                <DesksAdmin />
                            </div>
                        </details>

                        <details id="carrier-bays" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("carrier_bays_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("carrier_bays_settings_hint")}</p>
                                <CarrierBaysAdmin />
                            </div>
                        </details>

                        <details id="extra-box" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("extra_box_setting_title")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("extra_box_setting_hint")}</p>
                                <ExtraBoxSetting warehouses={rows.map(r => r.warehouse)} />
                            </div>
                        </details>

                        <details id="box-recommendation" className="admin-settings-subsection">
                            <summary className="admin-settings-subsection-summary">
                                <h3 className="admin-settings-section-title">{t("box_recommendation_settings")}</h3>
                            </summary>
                            <div className="admin-settings-subsection-body">
                                <p className="admin-settings-section-hint">{t("box_recommendation_settings_hint")}</p>

                                <h3 className="admin-settings-subsection-title">{t("dispatch_void_fill_pct")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_void_fill_pct_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="dispatch_void_fill_pct"
                                    placeholder={15}
                                    unit="%"
                                    step={1}
                                    max={100}
                                    onSaved={updateScalarNum("dispatch_void_fill_pct")}
                                />

                                <h3 className="admin-settings-subsection-title">{t("dispatch_packing_tolerance_mm")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_packing_tolerance_mm_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="dispatch_packing_tolerance_mm"
                                    placeholder={10}
                                    unit="mm"
                                    step={1}
                                    max={100}
                                    onSaved={updateScalarNum("dispatch_packing_tolerance_mm")}
                                />

                                <h3 className="admin-settings-subsection-title">{t("dispatch_stock_aware_recommendation")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_stock_aware_recommendation_hint")}</p>
                                <div className="admin-settings-table admin-settings-table--4col">
                                    <div className="admin-settings-row admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div>{t("dispatch_stock_aware_recommendation")}</div>
                                        <div />
                                        <div />
                                    </div>
                                    {rows.map(r => (
                                        <div key={r.warehouse} className="admin-settings-row">
                                            <div className="admin-settings-warehouse">{r.warehouse}</div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.dispatch_stock_aware_recommendation}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:dispatch_stock_aware_recommendation`}
                                                        onChange={e => toggle(r.warehouse, "dispatch_stock_aware_recommendation", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div />
                                            <div />
                                        </div>
                                    ))}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>

                                <h3 className="admin-settings-subsection-title">{t("dispatch_diagonal_recommendation")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_diagonal_recommendation_hint")}</p>
                                <div className="admin-settings-table admin-settings-table--4col">
                                    <div className="admin-settings-row admin-settings-row--head">
                                        <div>{t("warehouse_col")}</div>
                                        <div>{t("dispatch_diagonal_recommendation")}</div>
                                        <div />
                                        <div />
                                    </div>
                                    {rows.map(r => (
                                        <div key={r.warehouse} className="admin-settings-row">
                                            <div className="admin-settings-warehouse">{r.warehouse}</div>
                                            <div>
                                                <label className="admin-settings-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={r.dispatch_diagonal_recommendation}
                                                        disabled={readOnly || pendingKey === `${r.warehouse}:dispatch_diagonal_recommendation`}
                                                        onChange={e => toggle(r.warehouse, "dispatch_diagonal_recommendation", e.target.checked)}
                                                    />
                                                    <span className="admin-settings-slider" />
                                                </label>
                                            </div>
                                            <div />
                                            <div />
                                        </div>
                                    ))}
                                    {rows.length === 0 && (
                                        <div className="admin-settings-empty">{t("no_warehouses")}</div>
                                    )}
                                </div>

                                <h3 className="admin-settings-subsection-title">{t("dispatch_diagonal_tolerance_mm")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_diagonal_tolerance_mm_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="dispatch_diagonal_tolerance_mm"
                                    placeholder={10}
                                    unit="mm"
                                    step={1}
                                    max={100}
                                    onSaved={updateScalarNum("dispatch_diagonal_tolerance_mm")}
                                />

                                <h3 className="admin-settings-subsection-title">{t("dispatch_diagonal_bag_tolerance_mm")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_diagonal_bag_tolerance_mm_hint")}</p>
                                <ScalarNumEditor
                                    rows={rows}
                                    field="dispatch_diagonal_bag_tolerance_mm"
                                    placeholder={10}
                                    unit="mm"
                                    step={1}
                                    max={100}
                                    onSaved={updateScalarNum("dispatch_diagonal_bag_tolerance_mm")}
                                />

                                <h3 className="admin-settings-subsection-title">{t("dispatch_extra_protection_excluded_boxes")}</h3>
                                <p className="admin-settings-section-hint">{t("dispatch_extra_protection_excluded_boxes_hint")}</p>
                                <ExtraProtectionBoxesEditor
                                    rows={rows}
                                    onSaved={(warehouse, next) => setRows(curr => curr.map(r =>
                                        r.warehouse === warehouse
                                            ? { ...r, dispatch_extra_protection_excluded_boxes: next }
                                            : r,
                                    ))}
                                />
                            </div>
                        </details>
                    </div>
                </details>

                <details id="integrations" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><PlugZap size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("integrations_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("integrations_settings_hint")}</p>
                        <SalesChannelsAdmin />
                        <CarriersAdmin />
                    </div>
                </details>

                {/* BargainFox returns portal: reads follow admin.settings.integrations,
                    writes are Admin-only (fixed rule returns_config), so everyone else
                    sees it read-only. */}
                <details id="returns" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Undo2 size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("returns_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("returns_settings_hint")}</p>
                        <ReturnsSettings canEdit={isSuperAdmin} />
                    </div>
                </details>

                {/* Admin position only, like Network access below: the switch's route carries
                    an Admin floor (fixed rule saved_logins). */}
                {isSuperAdmin && (
                <details id="login-screen" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><KeyRound size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("login_screen_settings")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("login_screen_settings_hint")}</p>
                        <SavedLoginsSetting />
                    </div>
                </details>
                )}

                {/* Admin position only, like the Danger Zone: every route behind this section
                    carries an Admin floor (a fixed rule the matrix cannot widen), so showing
                    it to anyone else would only show them 403s. */}
                {isSuperAdmin && (
                <details id="network-access" className="admin-settings-section admin-settings-section--collapsible">
                    <summary className="admin-settings-section-summary">
                        <span className="admin-settings-section-icon"><Network size={28} strokeWidth={1.5} /></span>
                        <h2 className="admin-settings-section-title">{t("ip_allowlist_title")}</h2>
                    </summary>
                    <div className="admin-settings-section-body">
                        <p className="admin-settings-section-hint">{t("ip_allowlist_hint")}</p>
                        <IpAllowlistAdmin />
                    </div>
                </details>
                )}
                </>
            )}

            {editWarehouse && (() => {
                const isRename = editWarehouseName.trim() !== "" && editWarehouseName.trim() !== editWarehouse.warehouse;
                const renameConfirmOk = !isRename || editWarehouseRenameConfirm === editWarehouse.warehouse;
                return (
                <div className="modal-overlay" onClick={closeEditWarehouse}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("warehouse_edit")}</h2>
                            <button type="button" className="modal-close" onClick={closeEditWarehouse} aria-label={t("close")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <form className="modal-form" onSubmit={submitEditWarehouse}>
                                <label style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                                    {t("warehouse_col")}
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={editWarehouseName}
                                    onChange={e => setEditWarehouseName(e.target.value)}
                                    placeholder={t("warehouse_name_placeholder")}
                                    maxLength={64}
                                    required
                                />
                                <label style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                                    {t("country_col")}
                                </label>
                                <input
                                    type="text"
                                    value={editWarehouseCountry}
                                    onChange={e => setEditWarehouseCountry(e.target.value)}
                                    placeholder={t("country_placeholder")}
                                    list="known-countries-edit"
                                    maxLength={64}
                                    required
                                />
                                <datalist id="known-countries-edit">
                                    {Array.from(new Set(rows.map(r => r.country).filter(Boolean))).map(c => (
                                        <option key={c} value={c!} />
                                    ))}
                                </datalist>

                                {isRename && (
                                    <div style={{
                                        margin: "8px 0 0",
                                        padding: 12,
                                        border: "1px solid var(--color-warning, #f59e0b)",
                                        borderRadius: "var(--radius-sm, 6px)",
                                        background: "rgba(245, 158, 11, 0.08)",
                                    }}>
                                        <strong style={{ display: "block", marginBottom: 6 }}>
                                            {t("warehouse_rename_warning_title")}
                                        </strong>
                                        <p style={{ margin: "0 0 10px", fontSize: "0.85rem" }}>
                                            {t("warehouse_rename_warning_body")}
                                        </p>
                                        <label style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                                            {t("warehouse_rename_confirm_label", { name: editWarehouse.warehouse })}
                                        </label>
                                        <input
                                            type="text"
                                            value={editWarehouseRenameConfirm}
                                            onChange={e => setEditWarehouseRenameConfirm(e.target.value)}
                                            placeholder={editWarehouse.warehouse}
                                            required
                                        />
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    className="btn btn-primary btn-full"
                                    disabled={editWarehouseSubmitting
                                        || !editWarehouseName.trim()
                                        || !editWarehouseCountry.trim()
                                        || !renameConfirmOk
                                        || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {editWarehouseSubmitting ? t("saving") : t("save")}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
                );
            })()}

            {deleteWarehouse && (() => {
                const blockers = [
                    ...(deleteUsage?.postgres ?? []),
                    ...(deleteUsage?.mongo ?? []),
                ];
                const unchecked = deleteUsage?.unchecked ?? [];
                const canDelete = !!deleteUsage
                    && !deleteUsage.inUse
                    && deleteConfirm === deleteWarehouse.warehouse
                    && !deleteSubmitting
                    && !readOnly;
                return (
                <div className="modal-overlay" onClick={closeDeleteWarehouse}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("warehouse_delete")}</h2>
                            <button type="button" className="modal-close" onClick={closeDeleteWarehouse} aria-label={t("close")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            {deleteUsageLoading || !deleteUsage ? (
                                <p style={{ color: "var(--color-text-muted)" }}>
                                    {t("warehouse_delete_checking")}
                                </p>
                            ) : deleteUsage.inUse ? (
                                <>
                                    <p style={{ margin: "0 0 8px" }}>
                                        {t("warehouse_delete_in_use", { name: deleteWarehouse.warehouse })}
                                    </p>
                                    <ul style={{ margin: "0 0 10px", paddingLeft: 20, fontSize: "0.88rem" }}>
                                        {blockers.map(b => (
                                            <li key={b.name}>
                                                <strong>{b.name}</strong>{" — "}
                                                {b.capped
                                                    ? t("rows_count_capped", { count: String(b.count - 1) })
                                                    : t("rows_count", { count: String(b.count) })}
                                            </li>
                                        ))}
                                    </ul>
                                    {unchecked.length > 0 && (
                                        <p style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                                            {t("warehouse_delete_unchecked", { sources: unchecked.join(", ") })}
                                        </p>
                                    )}
                                    <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                                        {t("warehouse_delete_in_use_hint")}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p style={{ margin: "0 0 12px" }}>
                                        {t("warehouse_delete_unused", { name: deleteWarehouse.warehouse })}
                                    </p>
                                    <label style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                                        {t("warehouse_delete_confirm_label", { name: deleteWarehouse.warehouse })}
                                    </label>
                                    <input
                                        type="text"
                                        autoFocus
                                        value={deleteConfirm}
                                        onChange={e => setDeleteConfirm(e.target.value)}
                                        placeholder={deleteWarehouse.warehouse}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-full"
                                        style={{ marginTop: 12 }}
                                        disabled={!canDelete}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                        onClick={submitDeleteWarehouse}
                                    >
                                        {deleteSubmitting ? t("saving") : t("warehouse_delete")}
                                    </button>
                                    <p style={{ margin: "10px 0 0", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                                        {t("warehouse_delete_history_note")}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                );
            })()}

            {addWarehouseOpen && (
                <div className="modal-overlay" onClick={closeAddWarehouse}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("warehouse_add")}</h2>
                            <button type="button" className="modal-close" onClick={closeAddWarehouse} aria-label={t("close")}>
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <form className="modal-form" onSubmit={submitAddWarehouse}>
                                <label style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                                    {t("warehouse_col")}
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    value={addWarehouseName}
                                    onChange={e => setAddWarehouseName(e.target.value)}
                                    placeholder={t("warehouse_name_placeholder")}
                                    maxLength={64}
                                    required
                                />
                                <label style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                                    {t("country_col")}
                                </label>
                                <input
                                    type="text"
                                    value={addWarehouseCountry}
                                    onChange={e => setAddWarehouseCountry(e.target.value)}
                                    placeholder={t("country_placeholder")}
                                    list="known-countries"
                                    maxLength={64}
                                    required
                                />
                                <datalist id="known-countries">
                                    {Array.from(new Set(rows.map(r => r.country).filter(Boolean))).map(c => (
                                        <option key={c} value={c!} />
                                    ))}
                                </datalist>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-full"
                                    disabled={addWarehouseSubmitting || !addWarehouseName.trim() || !addWarehouseCountry.trim() || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {addWarehouseSubmitting ? t("saving") : t("warehouse_add")}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSettings;
